import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FormatChartData } from "../../../../components/recharts/functions";
import {
  lightenColor,
} from "../../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../../models/chart_model";
import type { CriticalPeriodKind } from "../../../../models/chart_model";
import { parseDeepLink } from "../../../../components/Search/Jvvnl/deepLink";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import { ExchangeColors } from "../../../Dashboard/Exchange3/FormatData";
import { formatDateForAPI, R_CHART_TYPES } from "../../../Rajasthan/const";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../utility/displayText";
import {
  getJvvnlDemand,
  getJvvnlCircles,
  getJvvnlTranscoSubstationsList,
  getJvvnlDiscomSubstations,
  getJvvnlFeederMetrics,
  getJvvnlFeeders,
  getJvvnlGeneration,
  getJvvnlPlants,
  getJvvnlSubstations,
  getJvvnlWeatherByLocation,
  getJvvnlEntityMetrics,
  JvvnlEntityMetricsResponse,
  getJvvnlLoadSummary,
  getJvvnlDemandForecast,
  getJvvnlGenerationForecast,
  getJvvnlFeederForecastByDate,
  JvvnlLoadSummaryRow,
  getJvvnlAggregatedWeather,
  getJvvnlDiscomSubDemandMetrics,
  getJvvnlTranscoDemandMetrics,
  getJvvnlCircleDemandMetrics,
  getJvvnlGenerationMetricsFlat,
  JvvnlGenerationMetricsResponse,
} from "../../../../Rest_api/RestApi";
import { getMonthStr } from "../../Utility/demandHelpers";
import {
  JvvnlDemandRow,
  JvvnlFeeder,
  JvvnlFeederMetricResult,
  JvvnlGenerationRow,
  JvvnlPlant,
  JvvnlPlantType,
  JvvnlSubstation,
  JvvnlDiscomSubstation,
} from "../../../../models/jvvnl";
import { useDiscovery } from "../../../../components/Search/Jvvnl/discovery";
import { useLegendFilter } from "../../../../hooks/useLegendFilter";
import { resolveEmbeddedFeederId, resolveNetTarget } from "./types";
import type { FeederChartMode } from "./types";
import {
  NET_CONSUMPTION_CIRCLE,
  JVVNL_DEMAND_UNITS,
  JVVNL_AGG_UNITS,
  JVVNL_GEN_UNITS,
  WEATHER_PARAMS,
  Intervals,
  GROUP_BY_OPTIONS,
} from "./constants";
import type {
  FunctionMode,
  GroupByOption,
  IntervalOption,
  IntervalKey,
} from "./constants";

// ============================================================================
// CONSTANTS & CONFIGURATION (engine-only)
// ============================================================================
const NET_CONSUMPTION_CIRCLE_ID = 1;

const GEN_UNITS = ["KW", "KWh", "MW", "MWh", "LU"] as const;

function unitDataKey(unit: string): keyof JvvnlGenerationRow {
  if (unit === "KW") return "generation_kw";
  if (unit === "KWh") return "generation_kwh";
  if (unit === "MWh") return "generation_mwh";
  if (unit === "LU") return "generation_lu";
  return "generation_mw";
}

const DEMAND_TAB_KEY = "Actual";
const LEGACY_DEMAND_TAB_KEY = "Feeder Demand";

// ============================================================================
// HELPER FUNCTIONS (engine-only)
// ============================================================================

function pickFirstString(
  source: Record<string, unknown> | undefined,
  keys: string[],
): string | undefined {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return undefined;
}

function getChartGroupingKeys(interval: IntervalOption): string[] {
  if (interval.key === "time") return ["date", "block"];
  if (interval.key === "date") return ["date"];
  return [interval.values[0]];
}

function getIntervalXKey(
  row: { date?: string; block?: number; month?: string },
  intervalKey: IntervalKey,
): string {
  if (intervalKey === "time") return `${row.date ?? ""}_${row.block ?? ""}`;
  if (intervalKey === "date") return row.date ?? "";
  return row.month ?? "";
}

// ============================================================================
// ENGINE HOOK
// ============================================================================

export function useJvvnlFeederChartData(mode: FeederChartMode) {
  const embedded = mode.kind === "popup" ? mode.embedded : undefined;
  const weatherParam = mode.kind === "popup" ? mode.weatherParam : undefined;
  const netVariable = mode.kind === "popup" ? mode.netVariable : undefined;
  const variablesOverride =
    mode.kind === "popup" ? mode.variablesOverride : undefined;
  const forecastVariants =
    mode.kind === "popup" ? mode.forecastVariants : undefined;
  const actualVariants =
    mode.kind === "popup" ? mode.actualVariants : undefined;
  const dateRangeOverride =
    mode.kind === "popup" ? mode.dateRangeOverride : undefined;
  const forecastDateOverride =
    mode.kind === "popup" ? mode.forecastDateOverride : undefined;
  const onEmbeddedFeederChange =
    mode.kind === "popup" ? mode.onEmbeddedFeederChange : undefined;
  const onEmbeddedFeederListChange =
    mode.kind === "popup" ? mode.onEmbeddedFeederListChange : undefined;
  const onEmbeddedNetChange =
    mode.kind === "popup" ? mode.onEmbeddedNetChange : undefined;

  const featureDiscovery = useDiscovery();
  // --- Redux State ---
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const effectiveTabKey = useMemo(() => {
    if (DefaultDates?.[DEMAND_TAB_KEY]) return DEMAND_TAB_KEY;
    if (DefaultDates?.[LEGACY_DEMAND_TAB_KEY]) return LEGACY_DEMAND_TAB_KEY;
    if (activeTab === LEGACY_DEMAND_TAB_KEY) return DEMAND_TAB_KEY;
    return activeTab;
  }, [DefaultDates, activeTab]);

  const currentRangeDate =
    dateRangeOverride ?? DefaultDates?.[effectiveTabKey]?.defaultRangeDate;

  // --- Local State ---
  const [selectedUnit, setSelectedUnit] = useState<string>(
    JVVNL_DEMAND_UNITS[0],
  );
  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupByOption>(
    embedded
      ? embedded.level === "plant"
        ? (GROUP_BY_OPTIONS.find((o) => o.rowKey === "group_generation") ??
          GROUP_BY_OPTIONS[0])
        : embedded.level === "feeder"
          ? (GROUP_BY_OPTIONS.find((o) => o.rowKey === "group_feeder") ??
            GROUP_BY_OPTIONS[0])
          : (GROUP_BY_OPTIONS.find(
              (o) => o.rowKey === "group_net_consumption",
            ) ?? GROUP_BY_OPTIONS[0])
      : (GROUP_BY_OPTIONS.find((o) => o.rowKey === "group_net_consumption") ??
          GROUP_BY_OPTIONS[0]),
  );
  const [selectedBlock, setSelectedBlock] = useState<{ value: number }[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  // Critical-periods toggle lifted to page scope so it persists across chart
  // remounts (filter key changes, Chart↔Table switches). In-memory only, so it
  // resets to OFF on full refresh / navigation away — per spec.
  // In embedded mode (NetConsumptionPopup) default the critical periods ON;
  // standalone page stays OFF until the user toggles it.
  const [criticalPeriodKinds, setCriticalPeriodKinds] = useState<
    Set<CriticalPeriodKind>
  >(() =>
    embedded
      ? new Set<CriticalPeriodKind>(["solar", "evening_ramp", "valley"])
      : new Set<CriticalPeriodKind>(),
  );
  const cpActive = criticalPeriodKinds.size > 0;
  const setCpActive = (active: boolean) =>
    setCriticalPeriodKinds(
      active
        ? new Set<CriticalPeriodKind>(["solar", "evening_ramp", "valley"])
        : new Set<CriticalPeriodKind>(),
    );
  const [selectedFunction, setSelectedFunction] =
    useState<FunctionMode>("Comparative");
  const [selectedVariables, setSelectedVariables] = useState<string[]>([
    "Actual",
  ]);
  const [selectedWeatherParams, setSelectedWeatherParams] = useState<string[]>([
    "temperature_2m",
  ]);

  // Bumped to imperatively open the FEEDER picker (discovery "Try it").
  const [feederOpenSignal, setFeederOpenSignal] = useState(0);

  // Discovery "Try it" actions — the floating tip card dispatches
  // `jvvnl:tip-action` with the tip id; activate the matching feature here.
  useEffect(() => {
    const onTipAction = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      switch (id) {
        case "multi-feeder":
          setFeederOpenSignal((n) => n + 1);
          break;
        case "weather-overlay":
          setSelectedVariables((prev) =>
            prev.includes("Weather") ? prev : [...prev, "Weather"],
          );
          break;
        case "critical-periods":
          setCpActive(true);
          break;
        case "cumulative-mode":
          setSelectedFunction("Cumulative");
          break;
        default:
          break;
      }
    };
    window.addEventListener("jvvnl:tip-action", onTipAction);
    return () => window.removeEventListener("jvvnl:tip-action", onTipAction);
  }, []);

  useEffect(() => {
    if (!embedded) return;
    const targetRowKey =
      embedded.level === "plant"
        ? "group_generation"
        : embedded.level === "feeder"
          ? "group_feeder"
          : "group_net_consumption";
    if (selectedGroupBy.rowKey === targetRowKey) return;
    const target = GROUP_BY_OPTIONS.find((o) => o.rowKey === targetRowKey);
    if (target) setSelectedGroupBy(target);
  }, [embedded, selectedGroupBy.rowKey]);

  // External weather param override (used by embedded popup sidebar)
  useEffect(() => {
    if (weatherParam == null) return;
    if (weatherParam === "") {
      setSelectedVariables((prev) => prev.filter((v) => v !== "Weather"));
      return;
    }
    setSelectedWeatherParams([weatherParam]);
    setSelectedVariables((prev) =>
      prev.includes("Weather") ? prev : [...prev, "Weather"],
    );
  }, [weatherParam]);

  // variablesOverride is rebuilt inline by the parent on every render (new
  // array ref), so depend on its serialized value and only update state when
  // the contents actually change — otherwise selectedVariables churns every
  // render and refires every downstream fetch effect.
  const variablesOverrideKey =
    variablesOverride != null ? variablesOverride.join("|") : null;
  useEffect(() => {
    if (variablesOverrideKey == null) return;
    const next =
      variablesOverrideKey === "" ? [] : variablesOverrideKey.split("|");
    setSelectedVariables((prev) =>
      prev.length === next.length && prev.every((v, i) => v === next[i])
        ? prev
        : next,
    );
  }, [variablesOverrideKey]);

  // Metadata State
  const [feeders, setFeeders] = useState<JvvnlFeeder[]>([]);
  const [feedersLoaded, setFeedersLoaded] = useState(false);
  const [substations, setSubstations] = useState<JvvnlSubstation[]>([]);
  const [substationsLoaded, setSubstationsLoaded] = useState(false);

  // Filter Options State
  const [selectedFeederOptions, setSelectedFeederOptions] = useState<
    { value: number; label: string }[]
  >([]);

  // Workflow-guard action bridge: the "Switch to daily" CTA on the
  // range-too-large guard dispatches `jvvnl:guard-action` — flip the interval
  // to the daily ("date") option. (State publishing lives below, after
  // chartData/loading are declared.)
  useEffect(() => {
    const onGuardAction = (e: Event) => {
      const guardId = (e as CustomEvent).detail as string;
      if (guardId === "range-too-large") {
        const daily = Intervals.find((i) => i.key === "date");
        if (daily) setSelectedInterval(daily);
      }
    };
    window.addEventListener("jvvnl:guard-action", onGuardAction);
    return () =>
      window.removeEventListener("jvvnl:guard-action", onGuardAction);
  }, []);

  const [selectedSubstationOptions, setSelectedSubstationOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const [selectedFeederSubstations, setSelectedFeederSubstations] = useState<
    { value: string; label: string }[]
  >([]);

  // ── Shared Circle filter (scopes transco/discom/feeder option lists) ──
  // Used by group_substation, group_discom_substation, group_feeder views.
  const [circleOptions, setCircleOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedCircle, setSelectedCircle] = useState<{
    value: number;
    label: string;
  } | null>(null);
  // Transco ids belonging to the selected circle (A2 cascade). null = unfiltered.
  const [circleTranscoIds, setCircleTranscoIds] = useState<Set<number> | null>(
    null,
  );
  // Feeder-view transco filter (feeder view has no transco box otherwise).
  const [selectedFeederTransco, setSelectedFeederTransco] = useState<{
    value: number;
    label: string;
  } | null>(null);

  // Net Consumption group mode
  const [netCircle, setNetCircle] = useState<{
    value: string;
    label: string;
  } | null>(NET_CONSUMPTION_CIRCLE);
  const [netTransco, setNetTransco] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [netDiscom, setNetDiscom] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [netLoadSummary, setNetLoadSummary] = useState<JvvnlLoadSummaryRow[]>(
    [],
  );

  // Discom Substation group mode
  const [discomSubstations, setDiscomSubstations] = useState<
    JvvnlDiscomSubstation[]
  >([]);
  const [discomSubstationsLoaded, setDiscomSubstationsLoaded] = useState(false);
  const [selectedTranscoForDiscom, setSelectedTranscoForDiscom] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedDiscomSubstations, setSelectedDiscomSubstations] = useState<
    { value: number; label: string }[]
  >([]);

  // In feeder mode the Circle is the implicit parent of every selectable
  // child. Whenever any child (transco / discom-sub / feeder) is selected,
  // auto-select the Circle so the chip reflects the active hierarchy.
  useEffect(() => {
    const hasChild =
      selectedTranscoForDiscom.length > 0 ||
      selectedDiscomSubstations.length > 0 ||
      selectedFeederOptions.length > 0;
    if (hasChild && !netCircle) setNetCircle(NET_CONSUMPTION_CIRCLE);
  }, [
    selectedTranscoForDiscom,
    selectedDiscomSubstations,
    selectedFeederOptions,
    netCircle,
  ]);

  // Data State
  const [rawData, setRawData] = useState<JvvnlDemandRow[]>([]);
  const [gssLoadData, setGssLoadData] = useState<
    (JvvnlLoadSummaryRow & { series_group: string })[]
  >([]);
  const [discomData, setDiscomData] = useState<JvvnlLoadSummaryRow[]>([]);
  const [discomSubLoadData, setDiscomSubLoadData] = useState<
    (JvvnlLoadSummaryRow & { series_group: string })[]
  >([]);
  const [weatherData, setWeatherData] = useState<any[]>([]);
  const [forecastRows, setForecastRows] = useState<any[]>([]);
  const [groupMetrics, setGroupMetrics] =
    useState<JvvnlFeederMetricResult | null>(null);
  const [netMetrics, setNetMetrics] =
    useState<JvvnlEntityMetricsResponse | null>(null);
  const [feederMetrics, setFeederMetrics] =
    useState<JvvnlFeederMetricResult | null>(null);
  const [genMetrics, setGenMetrics] =
    useState<JvvnlGenerationMetricsResponse | null>(null);

  // Generation State
  const [generationData, setGenerationData] = useState<JvvnlGenerationRow[]>(
    [],
  );
  const [allPlants, setAllPlants] = useState<JvvnlPlant[]>([]);
  const [plantsLoaded, setPlantsLoaded] = useState(false);
  const [selectedPlantTypeOptions, setSelectedPlantTypeOptions] = useState<
    { value: JvvnlPlantType; label: string }[]
  >([]);
  const [selectedPlantOptions, setSelectedPlantOptions] = useState<
    { value: number; label: string }[]
  >([]);
  // Generation-block circle filter (scopes the PLANT list by plant.circle_id).
  // Single-select; defaults to KOTPUTLI in the plant-defaults effect.
  const [selectedGenCircle, setSelectedGenCircle] = useState<{
    value: number;
    label: string;
  } | null>(null);

  // Chart State
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const weatherCacheRef = useRef(new Map<string, any[]>());
  // Last effective feeder-demand query key, to skip duplicate fetches when
  // unrelated deps tick.
  const feederDemandFetchKeyRef = useRef<string | null>(null);
  // True while a feeder-demand fetch is actually awaiting. Lets the dedup
  // short-circuit tell apart "a fetch is settling loading" from "the prior
  // fetch was cancelled mid-flight and will never clear loading".
  const feederDemandInFlightRef = useRef(false);
  // Per-fetch dedup keys: net-mode effects re-run on every netCircle/netTransco/
  // netDiscom/selectedCircle tick during the preselection cascade, producing
  // duplicate same-payload requests. Skip a fetch when its effective query key
  // (level|ids|range[|flags]) is unchanged from the prior render.
  const netMetricsFetchKeyRef = useRef<string | null>(null);
  const feederMetricsFetchKeyRef = useRef<string | null>(null);
  const groupMetricsFetchKeyRef = useRef<string | null>(null);
  const netLoadFetchKeyRef = useRef<string | null>(null);
  const netLoadInFlightRef = useRef(false);
  const discomSubLoadFetchKeyRef = useRef<string | null>(null);
  const discomSubLoadInFlightRef = useRef(false);
  const gssLoadFetchKeyRef = useRef<string | null>(null);
  const gssLoadInFlightRef = useRef(false);
  const circleLoadFetchKeyRef = useRef<string | null>(null);
  const circleLoadInFlightRef = useRef(false);
  const genFetchKeyRef = useRef<string | null>(null);
  const genInFlightRef = useRef(false);
  const netWeatherFetchKeyRef = useRef<string | null>(null);
  const netForecastFetchKeyRef = useRef<string | null>(null);
  const selectedBlockSet = useMemo(
    () =>
      selectedBlock.length > 0
        ? new Set(selectedBlock.map((blockOption) => blockOption.value))
        : null,
    [selectedBlock],
  );
  const rangeStart = currentRangeDate
    ? formatDateForAPI(currentRangeDate.startDate)
    : null;
  const rangeEnd = currentRangeDate
    ? formatDateForAPI(currentRangeDate.endDate)
    : null;
  // Memoize on the formatted date strings, not the currentRangeDate object —
  // dateRangeOverride/redux can hand us a fresh object with identical dates on
  // every render, which would otherwise refire every range-dependent effect.
  const formattedRange = useMemo(() => {
    if (!rangeStart || !rangeEnd) return null;
    return { startDate: rangeStart, endDate: rangeEnd };
  }, [rangeStart, rangeEnd]);

  // Publish current state to the discovery system for contextual tips +
  // workflow guards. Guard-relevant signals: interval/granularity, the selected
  // range span, the resolved result count, and whether a fetch has settled.
  useEffect(() => {
    featureDiscovery.setAppContext({
      selectedEntityCount: selectedFeederOptions.length,
      groupByKey: selectedGroupBy.rowKey,
      currentView: dataTab,
      functionMode: selectedFunction,
      intervalKey: selectedInterval?.key,
      rangeStartTs: currentRangeDate?.startDate
        ? new Date(currentRangeDate.startDate).getTime()
        : undefined,
      rangeEndTs: currentRangeDate?.endDate
        ? new Date(currentRangeDate.endDate).getTime()
        : undefined,
      resultCount: chartData.length,
      fetchSettled: !loading,
    });
  }, [
    selectedFeederOptions.length,
    selectedGroupBy.rowKey,
    dataTab,
    selectedFunction,
    selectedInterval?.key,
    currentRangeDate?.startDate,
    currentRangeDate?.endDate,
    chartData.length,
    loading,
    featureDiscovery,
  ]);

  // ============================================================================
  // 1. METADATA LOADING (Substations & Feeders)
  // ============================================================================

  // Load circle options + per-circle transco ids once. We fetch each circle's
  // transco list so we can both (a) scope option lists to the selected circle
  // and (b) backfill the circle from a selected transco/discom/feeder.
  const [circleIdToTranscoIds, setCircleIdToTranscoIds] = useState<
    Map<number, Set<number>>
  >(new Map());
  const [transcoToCircle, setTranscoToCircle] = useState<
    Map<number, { value: number; label: string }>
  >(new Map());
  // False when the per-circle transco endpoint returns the same transcos for
  // multiple circles (i.e. it doesn't actually filter by circle_id). In that
  // case the scope/backfill maps are meaningless, so we disable scoping and
  // backfill and just let the Circle box be a plain (KOTPUTLI-default) picker.
  const [reliableCircleScope, setReliableCircleScope] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getJvvnlCircles()
      .then(async (circles) => {
        if (cancelled) return;
        const opts = circles.map((c) => ({
          value: c.circle_id,
          label: c.circle_name,
        }));
        setCircleOptions(opts);
        // In embedded feeder mode the circle must reflect the clicked feeder's
        // real parent circle, resolved by applyBackfillCircle once the feeder's
        // discom/transco seed. Skip the hardcoded KOTPUTLI default here so it
        // doesn't win the `prev ?? c` race and pin every feeder to KOTPUTLI.
        if (embedded?.level !== "feeder") {
          const def =
            opts.find((o) => o.label === "KOTPUTLI") ?? opts[0] ?? null;
          setSelectedCircle((prev) => prev ?? def);
        }

        // Fetch each circle's transco list and build the scope + reverse maps.
        const lists = await Promise.all(
          circles.map((c) =>
            getJvvnlTranscoSubstationsList(c.circle_id)
              .then((res) => ({ circle: c, res }))
              .catch(() => ({ circle: c, res: { results: [] as any[] } })),
          ),
        );
        if (cancelled) return;
        const byCircle = new Map<number, Set<number>>();
        const reverse = new Map<number, { value: number; label: string }>();
        let ambiguous = false;
        for (const { circle, res } of lists) {
          const ids = new Set<number>();
          for (const r of res.results) {
            const tid = Number(r.id);
            if (isNaN(tid)) continue;
            ids.add(tid);
            // A transco already mapped to a different circle means the endpoint
            // isn't filtering by circle_id — scope data is unreliable.
            const existing = reverse.get(tid);
            if (existing && existing.value !== circle.circle_id) {
              ambiguous = true;
            }
            reverse.set(tid, {
              value: circle.circle_id,
              label: circle.circle_name,
            });
          }
          byCircle.set(circle.circle_id, ids);
        }
        setReliableCircleScope(!ambiguous);
        setCircleIdToTranscoIds(byCircle);
        setTranscoToCircle(reverse);
      })
      .catch(() => !cancelled && setCircleOptions([]));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded?.level]);

  // A2 cascade: transco ids belonging to the selected circle (from the maps
  // above). null = unfiltered (maps not loaded yet).
  useEffect(() => {
    const circleId = selectedCircle ? Number(selectedCircle.value) : undefined;
    if (
      !circleId ||
      isNaN(circleId) ||
      circleIdToTranscoIds.size === 0 ||
      !reliableCircleScope
    ) {
      setCircleTranscoIds(null);
      return;
    }
    setCircleTranscoIds(circleIdToTranscoIds.get(circleId) ?? new Set());
  }, [selectedCircle, circleIdToTranscoIds, reliableCircleScope]);

  // Backfill the Circle box from the selected entity so it reflects which circle
  // the current transco/discom/feeder belongs to. Only updates when the derived
  // circle differs from the current one (prevents render loops with the
  // clear-children onChange).
  const applyBackfillCircle = useCallback(
    (transcoId: number | null | undefined) => {
      if (transcoId == null || !reliableCircleScope) return;
      const c = transcoToCircle.get(Number(transcoId));
      if (!c) return;
      // Fill only when the circle is empty. Never override a circle the user has
      // already selected — otherwise picking a new circle gets reverted by the
      // auto-defaulted child's backfill.
      setSelectedCircle((prev) => prev ?? c);
    },
    [transcoToCircle, reliableCircleScope],
  );

  // Transco-substation view → circle from the (first) selected substation.
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_substation") return;
    applyBackfillCircle(selectedSubstationOptions[0]?.value);
  }, [selectedGroupBy.rowKey, selectedSubstationOptions, applyBackfillCircle]);

  // Discom-substation view → circle from the (first) selected transco.
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") return;
    applyBackfillCircle(selectedTranscoForDiscom[0]?.value);
  }, [selectedGroupBy.rowKey, selectedTranscoForDiscom, applyBackfillCircle]);

  // Feeder view → circle from the (first) selected discom-substation name,
  // mapped to its transco via the discomSubstations metadata.
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    const name = selectedFeederSubstations[0]?.value;
    if (name == null) return;
    const match = discomSubstations.find(
      (d) => d.discom_substation_name === name,
    );
    applyBackfillCircle(match?.transco_substation_id);
  }, [
    selectedGroupBy.rowKey,
    selectedFeederSubstations,
    discomSubstations,
    applyBackfillCircle,
  ]);

  // Discom-substation view → sync the TRANSCO box to the (first) selected discom
  // substation's parent transco. Runs even when a transco is already set so the
  // parent stays correct after a discom pick; guarded to avoid redundant sets.
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") return;
    const first = selectedDiscomSubstations[0];
    if (!first) return;
    const match = discomSubstations.find(
      (d) => String(d.discom_substation_id) === String(first.value),
    );
    const tid = match?.transco_substation_id;
    if (tid == null) return;
    if (
      selectedTranscoForDiscom.length === 1 &&
      selectedTranscoForDiscom[0].value === tid
    )
      return;
    setSelectedTranscoForDiscom([
      {
        value: tid,
        label: match?.transco_substation_name || `Transco ${tid}`,
      },
    ]);
  }, [
    selectedGroupBy.rowKey,
    selectedDiscomSubstations,
    selectedTranscoForDiscom,
    discomSubstations,
  ]);

  // Feeder view → backfill the DISCOM S/S box from the (first) selected feeder's
  // parent discom substation, when the user hasn't picked one.
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (selectedFeederSubstations.length > 0) return;
    const firstFeeder = selectedFeederOptions[0];
    if (!firstFeeder) return;
    const f = feeders.find((x) => String(x.id) === String(firstFeeder.value));
    const name = f?.discom_substation_name;
    if (!name) return;
    setSelectedFeederSubstations([{ value: name, label: name }]);
  }, [
    selectedGroupBy.rowKey,
    selectedFeederOptions,
    selectedFeederSubstations,
    feeders,
  ]);

  // Feeder view → backfill the TRANSCO box from the (first) selected discom
  // substation. Feeders carry discom_substation_id, so we resolve the discom by
  // name → its discom_substation_id → transco via the discomSubstations meta.
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (selectedFeederTransco) return;
    const name = selectedFeederSubstations[0]?.value;
    if (name == null) return;
    // Prefer matching by the feeder's own discom_substation_id (reliable across
    // endpoints); fall back to a name match.
    const feederForName = feeders.find(
      (f) => f.discom_substation_name === name,
    );
    const discomId = feederForName?.discom_substation_id;
    const match =
      (discomId != null &&
        discomSubstations.find(
          (d) => String(d.discom_substation_id) === String(discomId),
        )) ||
      discomSubstations.find((d) => d.discom_substation_name === name);
    if (match?.transco_substation_id != null) {
      setSelectedFeederTransco({
        value: match.transco_substation_id,
        label:
          match.transco_substation_name ||
          `Transco ${match.transco_substation_id}`,
      });
    }
  }, [
    selectedGroupBy.rowKey,
    selectedFeederSubstations,
    selectedFeederTransco,
    discomSubstations,
    feeders,
  ]);

  // Net Consumption view → backfill the TRANSCO box from the selected discom's
  // parent transco, when the user hasn't picked one.
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_net_consumption") return;
    if (netTransco || !netDiscom) return;
    const match = discomSubstations.find(
      (d) => d.discom_substation_id === netDiscom.value,
    );
    if (match?.transco_substation_id != null) {
      setNetTransco({
        value: match.transco_substation_id,
        label:
          match.transco_substation_name ||
          `Transco ${match.transco_substation_id}`,
      });
    }
  }, [selectedGroupBy.rowKey, netDiscom, netTransco, discomSubstations]);

  // Net Consumption view → backfill the CIRCLE box (name-valued) from the
  // selected transco (or selected discom's transco), when scope is reliable.
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_net_consumption") return;
    if (!reliableCircleScope) return;
    let transcoId: number | null = null;
    if (netTransco) transcoId = Number(netTransco.value);
    else if (netDiscom) {
      const match = discomSubstations.find(
        (d) => d.discom_substation_id === netDiscom.value,
      );
      transcoId = match?.transco_substation_id ?? null;
    }
    if (transcoId == null) return;
    const c = transcoToCircle.get(transcoId);
    if (!c) return;
    setNetCircle((prev) =>
      prev && prev.value === c.label
        ? prev
        : { value: c.label, label: c.label },
    );
  }, [
    selectedGroupBy.rowKey,
    netTransco,
    netDiscom,
    discomSubstations,
    transcoToCircle,
    reliableCircleScope,
  ]);

  useEffect(() => {
    if (
      (selectedGroupBy.rowKey !== "group_substation" &&
        selectedGroupBy.rowKey !== "group_feeder" &&
        selectedGroupBy.rowKey !== "group_discom_substation") ||
      substationsLoaded ||
      substations.length > 0
    )
      return;
    let cancelled = false;
    getJvvnlSubstations()
      .then((list) => {
        if (!cancelled) {
          setSubstations(list);
        }
      })
      .catch(() => {
        if (!cancelled) setSubstations([]);
      })
      .finally(() => {
        if (!cancelled) setSubstationsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedGroupBy.rowKey, substations.length, substationsLoaded]);

  useEffect(() => {
    if (
      selectedGroupBy.rowKey !== "group_feeder" ||
      feedersLoaded ||
      feeders.length > 0
    )
      return;
    let cancelled = false;
    getJvvnlFeeders()
      .then((list) => {
        if (!cancelled) setFeeders(list);
      })
      .catch((e) => {
        console.error("Failed to load JVVNL feeders", e);
        if (!cancelled) setFeeders([]);
      })
      .finally(() => {
        if (!cancelled) setFeedersLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [feeders.length, feedersLoaded, selectedGroupBy.rowKey]);

  // Discom Substations loading
  useEffect(() => {
    if (
      (selectedGroupBy.rowKey !== "group_discom_substation" &&
        selectedGroupBy.rowKey !== "group_net_consumption" &&
        selectedGroupBy.rowKey !== "group_feeder") ||
      discomSubstationsLoaded ||
      discomSubstations.length > 0
    )
      return;
    let cancelled = false;
    getJvvnlDiscomSubstations()
      .then((list) => {
        if (!cancelled) setDiscomSubstations(list);
      })
      .catch((e) => {
        console.error("Failed to load JVVNL discom substations", e);
        if (!cancelled) setDiscomSubstations([]);
      })
      .finally(() => {
        if (!cancelled) setDiscomSubstationsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    selectedGroupBy.rowKey,
    discomSubstationsLoaded,
    discomSubstations.length,
  ]);

  // Plants loading
  useEffect(() => {
    if (
      selectedGroupBy.rowKey !== "group_generation" &&
      selectedGroupBy.rowKey !== "group_net_consumption"
    )
      return;
    if (plantsLoaded) return;
    let cancelled = false;
    getJvvnlPlants()
      .then((list) => {
        if (!cancelled) setAllPlants(list);
      })
      .catch((e) => {
        console.error("Failed to load JVVNL plants", e);
        if (!cancelled) setAllPlants([]);
      })
      .finally(() => {
        if (!cancelled) setPlantsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedGroupBy.rowKey, plantsLoaded]);

  const plantLocation = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of allPlants) m.set(p.id, p.location);
    return m;
  }, [allPlants]);

  const selectedPlantTypes = useMemo(
    () => selectedPlantTypeOptions.map((o) => o.value),
    [selectedPlantTypeOptions],
  );

  const genCircleId = selectedGenCircle ? Number(selectedGenCircle.value) : null;

  const plantOptions = useMemo(() => {
    const filtered = allPlants.filter(
      (p) =>
        (selectedPlantTypes.length === 0 ||
          selectedPlantTypes.includes(p.plant_type)) &&
        (genCircleId == null || p.circle_id === genCircleId),
    );
    return filtered.map((p) => ({ value: p.id, label: p.name }));
  }, [allPlants, selectedPlantTypes, genCircleId]);

  const plantTypeOptions = useMemo(
    () =>
      Array.from(new Set(allPlants.map((p) => p.plant_type))).map((t) => ({
        value: t,
        label: t,
      })),
    [allPlants],
  );

  useEffect(() => {
    setSelectedPlantOptions((prev) =>
      prev.filter((o) => plantOptions.some((p) => p.value === o.value)),
    );
  }, [plantOptions]);

  // Tracks the location.search whose deep-link has already been applied, so the
  // plant-defaults effect can tell a deep-link is still pending. Set by the
  // deep-link effect below.
  const appliedDeepLinkRef = useRef<string | null>(null);

  const didInitPlantDefaultsRef = useRef(false);
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_generation") {
      didInitPlantDefaultsRef.current = false;
      return;
    }
    if (didInitPlantDefaultsRef.current) return;
    // Don't auto-fill defaults when an unconsumed plant deep-link is present —
    // the deep-link effect will select the specific plant. Prevents a clobber
    // when allPlants arrives in a later commit than the group-by switch.
    if (!embedded) {
      const link = parseDeepLink(window.location.search);
      if (
        link?.groupBy === "plant" &&
        appliedDeepLinkRef.current !== window.location.search
      ) {
        return;
      }
    }
    if (allPlants.length === 0) return;
    // Circle list is needed to default-select a circle; wait for it.
    if (circleOptions.length === 0) return;

    // Plant popup: deep-select the clicked plant + its own circle, instead of
    // forcing the KOTPUTLI default (which leaves the popup on the wrong circle
    // with an empty chart for plants outside KOTPUTLI).
    if (embedded?.level === "plant") {
      const plantId = Number(embedded.id);
      const plant = allPlants.find((p) => p.id === plantId);
      if (!plant) return; // wait until the plant list contains this id
      const circle =
        (plant.circle_id != null &&
          circleOptions.find((c) => Number(c.value) === plant.circle_id)) ||
        circleOptions[0];
      setSelectedGenCircle(circle);
      setSelectedPlantTypeOptions([
        { value: plant.plant_type, label: plant.plant_type },
      ]);
      setSelectedPlantOptions([{ value: plant.id, label: plant.name }]);
      didInitPlantDefaultsRef.current = true;
      return;
    }

    // Default circle = KOTPUTLI (match by name, case-insensitive).
    const kotputli =
      circleOptions.find((c) =>
        c.label.toUpperCase().includes("KOTPUTLI"),
      ) ?? circleOptions[0];
    setSelectedGenCircle(kotputli);
    // First 3 plants of that circle, alphabetical by name.
    const inCircle = allPlants
      .filter((p) => p.circle_id === Number(kotputli.value))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 3);
    const types = Array.from(new Set(inCircle.map((p) => p.plant_type))).map(
      (t) => ({ value: t, label: t }),
    );
    setSelectedPlantTypeOptions(types);
    setSelectedPlantOptions(
      inCircle.map((p) => ({ value: p.id, label: p.name })),
    );
    didInitPlantDefaultsRef.current = true;
  }, [selectedGroupBy.rowKey, allPlants, circleOptions, embedded]);

  const selectedPlantIds = useMemo(
    () => selectedPlantOptions.map((o) => o.value),
    [selectedPlantOptions],
  );

  // Generation data fetch
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_generation") {
      setGenerationData([]);
      genFetchKeyRef.current = null;
      return;
    }
    if (!formattedRange) return;
    if (!plantsLoaded) return;
    if (allPlants.length === 0) {
      setGenerationData([]);
      setLoading(false);
      genFetchKeyRef.current = null;
      return;
    }
    if (selectedPlantIds.length === 0) {
      setGenerationData([]);
      setLoading(false);
      genFetchKeyRef.current = null;
      return;
    }
    const fetchKey = `${[...selectedPlantIds].sort((a, b) => a - b).join(",")}|${formattedRange.startDate}|${formattedRange.endDate}`;
    if (genFetchKeyRef.current === fetchKey) {
      if (!genInFlightRef.current) setLoading(false);
      return;
    }
    genFetchKeyRef.current = fetchKey;

    let cancelled = false;
    setLoading(true);
    genInFlightRef.current = true;
    getJvvnlGeneration({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      plantIds: selectedPlantIds,
      plantLocation,
    })
      .then(({ rows }) => {
        if (!cancelled || genFetchKeyRef.current === fetchKey)
          setGenerationData(rows);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL generation:", err);
        if (!cancelled || genFetchKeyRef.current === fetchKey)
          setGenerationData([]);
      })
      .finally(() => {
        genInFlightRef.current = false;
        if (!cancelled || genFetchKeyRef.current === fetchKey)
          setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    plantsLoaded,
    allPlants.length,
    plantLocation,
    selectedGroupBy.rowKey,
    selectedPlantIds,
  ]);

  // Generation metrics (group_generation) via /generation-metrics/.
  // No plant-level entity option on this endpoint — defaults to circle/id=1.
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_generation") {
      setGenMetrics(null);
      return;
    }
    if (!formattedRange) return;
    let cancelled = false;
    getJvvnlGenerationMetricsFlat({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      level: "circle",
      ids: NET_CONSUMPTION_CIRCLE_ID,
    })
      .then((res) => {
        if (!cancelled) setGenMetrics(res);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL generation metrics:", err);
        if (!cancelled) setGenMetrics(null);
      });
    return () => {
      cancelled = true;
    };
  }, [formattedRange, selectedGroupBy.rowKey]);

  // ============================================================================
  // 2. MEMOS & SELECTIONS
  // ============================================================================
  const feederLocation = useMemo(
    () => new Map(feeders.map((f) => [f.id, f.location])),
    [feeders],
  );
  const substationNameByFeederId = useMemo(
    () =>
      new Map(
        feeders.map((f) => [
          f.id,
          pickFirstString(f as any, [
            "substation_name",
            "substation",
            "location_name",
          ]) || "Unknown Substation",
        ]),
      ),
    [feeders],
  );
  const substationById = useMemo(
    () => new Map(substations.map((s) => [s.id, s])),
    [substations],
  );
  const feederOptions = useMemo(
    () => feeders.map((f) => ({ value: f.id, label: f.name })),
    [feeders],
  );

  const substationOptions = useMemo(() => {
    const list = circleTranscoIds
      ? substations.filter((s) => circleTranscoIds.has(Number(s.id)))
      : substations;
    return list.map((s) => ({ value: s.id, label: s.station }));
  }, [substations, circleTranscoIds]);

  // Discom-substation names allowed under the selected circle + feeder-view
  // transco. Feeders only carry discom_substation_name, so we scope via the
  // discomSubstations metadata (transco_substation_id → name). null = no scope.
  const feederAllowedDiscomNames = useMemo(() => {
    const transcoId = selectedFeederTransco
      ? Number(selectedFeederTransco.value)
      : null;
    if (!circleTranscoIds && transcoId == null) return null;
    const names = new Set<string>();
    for (const d of discomSubstations) {
      const tid = Number(d.transco_substation_id);
      if (transcoId != null) {
        if (tid !== transcoId) continue;
      } else if (circleTranscoIds && !circleTranscoIds.has(tid)) {
        continue;
      }
      if (d.discom_substation_name) names.add(d.discom_substation_name);
    }
    return names;
  }, [discomSubstations, circleTranscoIds, selectedFeederTransco]);

  // Unique substation names from feeders (for feeder-mode substation filter)
  const feederSubstationOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { value: string; label: string }[] = [];
    for (const f of feeders) {
      const name = f.discom_substation_name;
      if (!name || seen.has(name)) continue;
      if (feederAllowedDiscomNames && !feederAllowedDiscomNames.has(name))
        continue;
      seen.add(name);
      out.push({ value: name, label: name });
    }
    return out;
  }, [feeders, feederAllowedDiscomNames]);

  // ── Net Consumption: derived option lists ──
  // Net Consumption identifies the circle by NAME (resolveNetTarget reads
  // netCircle.value as the circle name), so option values are circle names.
  const netCircleOptions = useMemo(
    () => circleOptions.map((c) => ({ value: c.label, label: c.label })),
    [circleOptions],
  );

  // Circle id for the net-consumption circle (resolved from its name). The new
  // JVVNL API selects by level+ids, so name must be mapped to a primary key.
  const netCircleId = useMemo<number | undefined>(() => {
    if (!netCircle) return undefined;
    const circ = circleOptions.find((c) => c.label === netCircle.value);
    return circ ? Number(circ.value) : undefined;
  }, [netCircle, circleOptions]);

  // Transco ids for the net-consumption circle (by name). null = unscoped.
  const netCircleTranscoIds = useMemo<Set<number> | null>(() => {
    if (!reliableCircleScope || !netCircle) return null;
    const circ = circleOptions.find((c) => c.label === netCircle.value);
    if (!circ) return null;
    return circleIdToTranscoIds.get(Number(circ.value)) ?? new Set();
  }, [reliableCircleScope, netCircle, circleOptions, circleIdToTranscoIds]);

  const netTranscoOptions = useMemo(() => {
    const seen = new Set<number>();
    const out: { value: number; label: string }[] = [];
    for (const d of discomSubstations) {
      if (d.transco_substation_id == null) continue;
      if (
        netCircleTranscoIds &&
        !netCircleTranscoIds.has(Number(d.transco_substation_id))
      )
        continue;
      if (seen.has(d.transco_substation_id)) continue;
      seen.add(d.transco_substation_id);
      out.push({
        value: d.transco_substation_id,
        label:
          d.transco_substation_name || `Transco ${d.transco_substation_id}`,
      });
    }
    return out;
  }, [discomSubstations, netCircleTranscoIds]);

  const netDiscomOptions = useMemo(() => {
    const allowedTranscoIds = new Set(netTranscoOptions.map((t) => t.value));
    const filtered = netTransco
      ? discomSubstations.filter(
          (d) => d.transco_substation_id === netTransco.value,
        )
      : discomSubstations.filter(
          (d) =>
            d.transco_substation_id != null &&
            allowedTranscoIds.has(d.transco_substation_id),
        );
    return filtered.map((d) => ({
      value: d.discom_substation_id,
      label: d.discom_substation_name,
    }));
  }, [discomSubstations, netTransco, netTranscoOptions]);

  const netSeriesLabel = useMemo(() => {
    if (netDiscom) return `Discom Substation - ${netDiscom.label}`;
    if (netTransco) return `Transco - ${netTransco.label}`;
    return `Circle - ${netCircle?.label ?? "KOTPUTLI"}`;
  }, [netCircle, netTransco, netDiscom]);

  // Net Consumption at discom_sub level is raw kW (no real MW), so the chart
  // must plot/label kW to match the metric cards. circle/transco are real MW.
  const netIsKw = !!netDiscom;

  // Transco substations list available for Discom-Substation group filter
  const transcoOptionsForDiscom = useMemo(() => {
    const seen = new Set<number>();
    const out: { value: number; label: string }[] = [];
    for (const d of discomSubstations) {
      if (d.transco_substation_id == null) continue;
      if (
        circleTranscoIds &&
        !circleTranscoIds.has(Number(d.transco_substation_id))
      )
        continue;
      if (seen.has(d.transco_substation_id)) continue;
      seen.add(d.transco_substation_id);
      out.push({
        value: d.transco_substation_id,
        label:
          d.transco_substation_name || `Transco ${d.transco_substation_id}`,
      });
    }
    return out;
  }, [discomSubstations, circleTranscoIds]);

  // Discom substations filtered by selected transco (empty = all)
  const filteredDiscomSubstationOptions = useMemo(() => {
    const list =
      selectedTranscoForDiscom.length === 0
        ? circleTranscoIds
          ? discomSubstations.filter((d) =>
              circleTranscoIds.has(Number(d.transco_substation_id)),
            )
          : discomSubstations
        : discomSubstations.filter((d) =>
            selectedTranscoForDiscom.some(
              (t) => t.value === d.transco_substation_id,
            ),
          );
    return list.map((d) => ({
      value: d.discom_substation_id,
      label: d.discom_substation_name,
    }));
  }, [discomSubstations, selectedTranscoForDiscom, circleTranscoIds]);

  const filteredFeederOptions = useMemo(() => {
    if (selectedFeederSubstations.length === 0) {
      if (!feederAllowedDiscomNames) return feederOptions;
      return feeders
        .filter((f) => feederAllowedDiscomNames.has(f.discom_substation_name))
        .map((f) => ({ value: f.id, label: f.name }));
    }
    const selected = new Set(selectedFeederSubstations.map((s) => s.value));
    return feeders
      .filter((f) => selected.has(f.discom_substation_name))
      .map((f) => ({ value: f.id, label: f.name }));
  }, [
    feeders,
    feederOptions,
    selectedFeederSubstations,
    feederAllowedDiscomNames,
  ]);

  // Default: first transco + first discom substation under it, ONCE on initial load
  const didInitDiscomSubDefaultsRef = useRef(false);
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") return;
    if (didInitDiscomSubDefaultsRef.current) return;
    if (discomSubstations.length === 0) return;
    // Wait until the circle scope is resolved so the default lands inside the
    // selected (default KOTPUTLI) circle. Only wait while scope is reliable but
    // still loading — if the endpoint doesn't filter (unreliable), don't block.
    if (selectedCircle && reliableCircleScope && circleTranscoIds === null)
      return;
    // A pending discom-substation deep-link will set the selection itself; don't
    // let the defaults race ahead and clobber it.
    if (!embedded) {
      const link = parseDeepLink(window.location.search);
      if (
        link?.groupBy === "discom_substation" &&
        appliedDeepLinkRef.current !== window.location.search
      ) {
        return;
      }
    }
    const firstTransco = transcoOptionsForDiscom[0];
    if (!firstTransco) return;
    setSelectedTranscoForDiscom([firstTransco]);
    const firstDiscom = discomSubstations
      .filter((d) => d.transco_substation_id === firstTransco.value)
      .map((d) => ({
        value: d.discom_substation_id,
        label: d.discom_substation_name,
      }))[0];
    setSelectedDiscomSubstations(firstDiscom ? [firstDiscom] : []);
    didInitDiscomSubDefaultsRef.current = true;
  }, [
    selectedGroupBy.rowKey,
    discomSubstations,
    transcoOptionsForDiscom,
    embedded,
    selectedCircle,
    circleTranscoIds,
    reliableCircleScope,
  ]);

  // Keep discom substation selection valid relative to transco filter
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") return;
    if (selectedTranscoForDiscom.length === 0) return;
    const allowed = new Set(
      filteredDiscomSubstationOptions.map((o) => o.value),
    );
    setSelectedDiscomSubstations((prev) => {
      const valid = prev.filter((o) => allowed.has(o.value));
      return valid.length > 0
        ? valid
        : filteredDiscomSubstationOptions.slice(0, 1);
    });
  }, [
    selectedGroupBy.rowKey,
    selectedTranscoForDiscom,
    filteredDiscomSubstationOptions,
  ]);

  const didInitFeederDefaultsRef = useRef(false);

  // Default: pick first substation + its feeders, ONCE on initial load
  // Skip in embedded feeder mode — seeding is handled by embeddedSeededRef effect.
  useEffect(() => {
    if (embedded?.level === "feeder") return;
    if (didInitFeederDefaultsRef.current) return;
    if (feeders.length === 0) return;
    // Wait until the circle scope is resolved (needs discomSubstations too, since
    // feeders map to circle via discom name → transco) so the default lands in
    // the selected (default KOTPUTLI) circle. Only wait while scope is reliable
    // but still loading — if the endpoint doesn't filter, don't block.
    if (
      selectedCircle &&
      reliableCircleScope &&
      (circleTranscoIds === null || discomSubstations.length === 0)
    )
      return;
    const firstSub = feederSubstationOptions[0];
    if (!firstSub) return;
    const subDefaults = [firstSub];
    setSelectedFeederSubstations(subDefaults);
    const defaultFeeders = feeders
      .filter((f) => f.discom_substation_name === firstSub.value)
      .map((f) => ({ value: f.id, label: f.name }));
    setSelectedFeederOptions(defaultFeeders);
    didInitFeederDefaultsRef.current = true;
  }, [
    feeders,
    feederSubstationOptions,
    embedded,
    selectedCircle,
    circleTranscoIds,
    discomSubstations,
    reliableCircleScope,
  ]);

  // Keep feeder selection aligned with selected feeder-substation(s).
  // If current feeder selection becomes invalid for chosen substations,
  // default to all feeders inside the selected substation(s).
  // Skip while embedded seeding hasn't completed yet to avoid overwriting it.
  useEffect(() => {
    if (embedded?.level === "feeder" && !embeddedSeededRef.current) return;
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (selectedFeederSubstations.length === 0) return;

    const selectedSubNames = new Set(
      selectedFeederSubstations.map((s) => s.value),
    );
    const feedersInSelectedSubstations = feeders
      .filter((f) => selectedSubNames.has(f.discom_substation_name))
      .map((f) => ({ value: f.id, label: f.name }));

    setSelectedFeederOptions((prev) => {
      const allowedIds = new Set(
        feedersInSelectedSubstations.map((f) => f.value),
      );
      const validPrev = prev.filter((f) => allowedIds.has(f.value));
      return validPrev.length > 0 ? validPrev : feedersInSelectedSubstations;
    });
  }, [feeders, selectedGroupBy.rowKey, selectedFeederSubstations, embedded]);

  useEffect(() => {
    setSelectedSubstationOptions((prev) => {
      const filtered = prev.filter((o) =>
        substationOptions.some((s) => s.value === o.value),
      );
      // Only auto-pick a default substation while a circle is selected. When the
      // user clears the circle, leave the substation empty (no refill loop).
      return filtered.length === 0 &&
        substationOptions.length > 0 &&
        selectedCircle != null
        ? [substationOptions[0]]
        : filtered;
    });
  }, [substationOptions, selectedCircle]);

  const generationOnlyMode = selectedGroupBy.rowKey === "group_generation";

  // Adjust units based on group by / variable mode
  useEffect(() => {
    if (generationOnlyMode) {
      if (!JVVNL_GEN_UNITS.includes(selectedUnit as any))
        setSelectedUnit(JVVNL_GEN_UNITS[0]);
      return;
    }
    if (selectedGroupBy.rowKey === "group_net_consumption") {
      const allowed = netIsKw ? ["KW", "KWh"] : ["MW", "MWh"];
      if (!allowed.includes(selectedUnit)) setSelectedUnit(allowed[0]);
      return;
    }
    if (
      selectedGroupBy.rowKey === "group_feeder" ||
      selectedGroupBy.rowKey === "group_discom_substation"
    ) {
      if (!JVVNL_DEMAND_UNITS.includes(selectedUnit as any))
        setSelectedUnit(JVVNL_DEMAND_UNITS[0]);
    } else {
      if (!JVVNL_AGG_UNITS.includes(selectedUnit as any))
        setSelectedUnit(JVVNL_AGG_UNITS[0]);
    }
  }, [selectedGroupBy.rowKey, selectedUnit, generationOnlyMode, netIsKw]);

  // Extract selected IDs
  const selectedFeederIds = useMemo(
    () => selectedFeederOptions.map((o) => o.value),
    [selectedFeederOptions],
  );
  const selectedSubstationIds = useMemo(
    () => selectedSubstationOptions.map((o) => o.value),
    [selectedSubstationOptions],
  );
  const selectedSubstationNames = useMemo(
    () =>
      selectedSubstationIds
        .map((id) => substationById.get(id)?.station)
        .filter(Boolean) as string[],
    [selectedSubstationIds, substationById],
  );

  const selectedDiscomSubstationIds = useMemo(
    () => selectedDiscomSubstations.map((o) => o.value),
    [selectedDiscomSubstations],
  );

  const effectiveFeederIds = useMemo(() => {
    if (selectedGroupBy.rowKey === "group_feeder") return selectedFeederIds;
    if (selectedGroupBy.rowKey === "group_substation") {
      if (selectedSubstationNames.length === 0) return [];
      return feeders
        .filter((f) =>
          selectedSubstationNames.includes(
            substationNameByFeederId.get(f.id) || "Unknown Substation",
          ),
        )
        .map((f) => f.id);
    }
    return [];
  }, [
    feeders,
    selectedFeederIds,
    selectedGroupBy.rowKey,
    selectedSubstationNames,
    substationNameByFeederId,
  ]);

  const selectedWeatherParam =
    selectedWeatherParams[0] || WEATHER_PARAMS[0].key;
  const selectedWeatherParamMeta = useMemo(
    () =>
      WEATHER_PARAMS.find((w) => w.key === selectedWeatherParam) ||
      WEATHER_PARAMS[0],
    [selectedWeatherParam],
  );
  const selectedWeatherMetricLabel =
    selectedWeatherParamMeta.chartLabel ||
    selectedWeatherParamMeta.label ||
    selectedWeatherParam;
  const chartGroupingKeys = useMemo(
    () => getChartGroupingKeys(selectedInterval),
    [selectedInterval],
  );
  const getXKeyForInterval = useCallback(
    (row: { date?: string; block?: number; month?: string }) =>
      getIntervalXKey(row, selectedInterval.key as IntervalKey),
    [selectedInterval.key],
  );

  const weatherSeries = useMemo(() => {
    if (!selectedVariables.includes("Weather")) return [];
    // Cumulative collapses all entity weather lines into one averaged "Total".
    if (selectedFunction === "Cumulative")
      return [{ value: "total", label: "Total" }];
    if (selectedGroupBy.rowKey === "group_feeder") return selectedFeederOptions;
    if (selectedGroupBy.rowKey === "group_substation")
      return selectedSubstationOptions;
    if (selectedGroupBy.rowKey === "group_discom_substation")
      return selectedDiscomSubstations;
    if (selectedGroupBy.rowKey === "group_net_consumption") {
      return [{ value: "net", label: netSeriesLabel }];
    }
    if (
      selectedGroupBy.rowKey === "group_generation" &&
      selectedPlantOptions.length > 0
    ) {
      return selectedPlantOptions.map((p) => ({
        value: `plant_${p.value}`,
        label: p.label,
      }));
    }
    return [
      {
        value: Number(selectedCircle?.value ?? 1),
        label: selectedCircle?.label ?? "KOTPUTLI",
      },
    ];
  }, [
    selectedGroupBy.rowKey,
    selectedVariables,
    selectedFeederOptions,
    selectedSubstationOptions,
    selectedPlantOptions,
    selectedDiscomSubstations,
    netSeriesLabel,
    selectedFunction,
    selectedCircle,
  ]);

  const forecastSeries = useMemo(() => {
    if (selectedGroupBy.rowKey === "group_net_consumption") {
      const baseLabel =
        selectedFunction === "Cumulative" ? "Total" : netSeriesLabel;
      const allVariants = ["Demand", "Generation", "Net Load"];
      const variants =
        forecastVariants && forecastVariants.length > 0
          ? allVariants.filter((v) => forecastVariants.includes(v))
          : netVariable
            ? [netVariable]
            : allVariants;
      return variants.map((v) => {
        const tag =
          v === "Net Load"
            ? "Net"
            : v === "Generation"
              ? "Generation"
              : "Demand";
        return {
          value: `${baseLabel} (${tag})` as any,
          label: `${baseLabel} ${tag}`,
        };
      });
    }
    // Non-net Cumulative: one combined forecast line (matches forecast_Total).
    if (selectedFunction === "Cumulative")
      return [{ value: "Total" as any, label: "Total" }];
    if (selectedGroupBy.rowKey === "group_feeder")
      return selectedFeederOptions.map((o) => ({
        value: o.value as any,
        label: o.label,
      }));
    if (selectedGroupBy.rowKey === "group_discom")
      return [
        {
          value: (selectedCircle?.label ?? "KOTPUTLI") as any,
          label: selectedCircle?.label ?? "KOTPUTLI",
        },
      ];
    if (selectedGroupBy.rowKey === "group_substation")
      return selectedSubstationOptions.map((o) => ({
        value: o.value as any,
        label: o.label,
      }));
    if (selectedGroupBy.rowKey === "group_discom_substation")
      return selectedDiscomSubstations.map((o) => ({
        value: o.value as any,
        label: o.label,
      }));
    if (selectedGroupBy.rowKey === "group_generation")
      return selectedPlantOptions.map((o) => ({
        value: o.value as any,
        label: o.label,
      }));
    return selectedSubstationOptions;
  }, [
    selectedGroupBy.rowKey,
    selectedSubstationOptions,
    selectedDiscomSubstations,
    selectedFeederOptions,
    selectedPlantOptions,
    selectedFunction,
    netSeriesLabel,
    netVariable,
    forecastVariants,
    selectedCircle,
  ]);

  // ============================================================================
  // 3. DATA FETCHING (Demand, GSS Load, Discom Energy)
  // ============================================================================
  // Actual Data
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (!formattedRange) {
      setLoading(false);
      return;
    }
    // After embedded seeding completes, selectedFeederIds is the source of truth.
    // Only fall back to embedded.id before seeding fires (initial load).
    const feederIdsForFetch =
      selectedFeederIds.length > 0
        ? selectedFeederIds
        : !embeddedSeededRef.current
          ? (() => {
              const eid = resolveEmbeddedFeederId(embedded);
              return eid != null ? [eid] : [];
            })()
          : [];
    if (feederIdsForFetch.length === 0) {
      setRawData([]);
      setLoading(false);
      return;
    }

    // Several deps (selectedFeederIds seeding, feederLocation, range) can tick
    // separately for the same effective query; skip if nothing changed.
    const fetchKey = `${formattedRange.startDate}|${formattedRange.endDate}|${[...feederIdsForFetch].sort((a, b) => a - b).join(",")}`;
    if (feederDemandFetchKeyRef.current === fetchKey) {
      // Same query as a prior render. If that prior fetch is still genuinely
      // in flight, let its .finally clear loading. But if it was cancelled by a
      // re-render before resolving (e.g. embedded feeder seeding flips
      // selectedFeederIds between mount and the fetch settling), nothing will
      // ever clear loading — so clear it here.
      if (!feederDemandInFlightRef.current) setLoading(false);
      return;
    }
    feederDemandFetchKeyRef.current = fetchKey;

    let cancelled = false;
    setLoading(true);
    feederDemandInFlightRef.current = true;
    getJvvnlDemand({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      feederIds: feederIdsForFetch,
      feederLocation,
    })
      .then(({ rows }) => {
        // Commit if this run wasn't superseded by a DIFFERENT query. When a
        // re-render dedups to the same fetchKey it cancels this run but starts
        // no new fetch (group→Feeder seeding race), so `cancelled` alone would
        // wrongly drop the only response and leave rawData empty until the user
        // changes the date. Commit as long as our key is still the active one.
        if (!cancelled || feederDemandFetchKeyRef.current === fetchKey)
          setRawData(rows);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL Actual:", err);
        if (!cancelled || feederDemandFetchKeyRef.current === fetchKey)
          setRawData([]);
      })
      .finally(() => {
        feederDemandInFlightRef.current = false;
        // Clear loading even if cancelled, as long as no newer fetch supplanted
        // this query key.
        if (!cancelled || feederDemandFetchKeyRef.current === fetchKey)
          setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    feederLocation,
    selectedFeederIds,
    selectedGroupBy.rowKey,
    embedded?.id,
    embedded?.level,
    embedded,
  ]);

  // Metrics: split per mode so each effect only depends on its own inputs.
  // The old single effect listed every mode's deps, so an unrelated tick (e.g.
  // selectedCircle while in net mode) re-ran it and re-issued the same
  // entity-metrics request. Each effect now carries a fetch-key guard too, to
  // drop duplicate same-payload fetches during the preselection cascade.

  // Net mode → /entity-metrics/
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_net_consumption") return;
    if (!formattedRange) return;
    setGroupMetrics(null);
    setFeederMetrics(null);
    // Sibling state was just cleared; force their next activation to refetch.
    feederMetricsFetchKeyRef.current = null;
    groupMetricsFetchKeyRef.current = null;
    const { level, ids } = resolveNetTarget(
      netDiscom,
      netTransco,
      netCircle,
      netCircleId,
    );
    const fetchKey = `${level}|${(Array.isArray(ids) ? ids : [ids]).join(",")}|${formattedRange.startDate}|${formattedRange.endDate}`;
    if (netMetricsFetchKeyRef.current === fetchKey) return;
    netMetricsFetchKeyRef.current = fetchKey;

    let cancelled = false;
    getJvvnlEntityMetrics({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      level,
      ids,
    })
      .then((res) => {
        if (!cancelled || netMetricsFetchKeyRef.current === fetchKey)
          setNetMetrics(res);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL entity metrics:", err);
        if (!cancelled || netMetricsFetchKeyRef.current === fetchKey)
          setNetMetrics(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    netCircle,
    netTransco,
    netDiscom,
    netCircleId,
  ]);

  // Feeder mode → /jvvnl-metrics/feeder/
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (!formattedRange) return;
    setNetMetrics(null);
    setGroupMetrics(null);
    netMetricsFetchKeyRef.current = null;
    groupMetricsFetchKeyRef.current = null;
    const feederIds =
      selectedFeederIds.length > 0
        ? selectedFeederIds
        : !embeddedSeededRef.current
          ? (() => {
              const eid = resolveEmbeddedFeederId(embedded);
              return eid != null ? [eid] : [];
            })()
          : [];
    if (feederIds.length === 0) {
      setFeederMetrics(null);
      return;
    }
    const fetchKey = `feeder|${[...feederIds].sort((a, b) => a - b).join(",")}|${formattedRange.startDate}|${formattedRange.endDate}`;
    if (feederMetricsFetchKeyRef.current === fetchKey) return;
    feederMetricsFetchKeyRef.current = fetchKey;

    let cancelled = false;
    getJvvnlFeederMetrics({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      level: "feeder",
      ids: feederIds,
      combine: true,
    })
      .then((res) => {
        if (!cancelled || feederMetricsFetchKeyRef.current === fetchKey)
          setFeederMetrics(res?.results?.[0] ?? null);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL feeder metrics:", err);
        if (!cancelled || feederMetricsFetchKeyRef.current === fetchKey)
          setFeederMetrics(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedFeederIds,
    embedded?.id,
    embedded?.level,
    embedded,
  ]);

  // Group modes (circle / transco / discom_sub) → /jvvnl-metrics/*
  useEffect(() => {
    const isGroupDiscomSub =
      selectedGroupBy.rowKey === "group_discom_substation";
    const isGroupTransco = selectedGroupBy.rowKey === "group_substation";
    const isGroupCircle = selectedGroupBy.rowKey === "group_discom";
    if (!isGroupDiscomSub && !isGroupTransco && !isGroupCircle) return;
    if (!formattedRange) return;
    setNetMetrics(null);
    setFeederMetrics(null);
    netMetricsFetchKeyRef.current = null;
    feederMetricsFetchKeyRef.current = null;
    const ids = isGroupDiscomSub
      ? selectedDiscomSubstationIds
      : isGroupTransco
        ? selectedSubstationIds
        : [Number(selectedCircle?.value ?? NET_CONSUMPTION_CIRCLE_ID)];
    if (!ids || ids.length === 0) {
      setGroupMetrics(null);
      return;
    }
    const level = isGroupCircle
      ? "circle"
      : isGroupTransco
        ? "transco"
        : "discom_sub";
    const fetchKey = `${level}|${[...ids].join(",")}|${formattedRange.startDate}|${formattedRange.endDate}`;
    if (groupMetricsFetchKeyRef.current === fetchKey) return;
    groupMetricsFetchKeyRef.current = fetchKey;

    let cancelled = false;
    const fetcher = isGroupCircle
      ? getJvvnlCircleDemandMetrics({
          startDate: formattedRange.startDate,
          endDate: formattedRange.endDate,
          level: "circle",
          ids,
          combine: true,
        })
      : isGroupTransco
        ? getJvvnlTranscoDemandMetrics({
            startDate: formattedRange.startDate,
            endDate: formattedRange.endDate,
            level: "transco",
            ids,
            combine: true,
          })
        : getJvvnlDiscomSubDemandMetrics({
            startDate: formattedRange.startDate,
            endDate: formattedRange.endDate,
            level: "discom_sub",
            ids,
            combine: true,
          });
    fetcher
      .then((res) => {
        // Commit while our key is still active: a same-key re-render cancels
        // this run but starts no new fetch, so `cancelled` alone would drop the
        // only response and leave the metric cards spinning forever.
        if (!cancelled || groupMetricsFetchKeyRef.current === fetchKey)
          setGroupMetrics(res?.results?.[0] ?? null);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL group metrics:", err);
        if (!cancelled || groupMetricsFetchKeyRef.current === fetchKey)
          setGroupMetrics(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedCircle,
    selectedSubstationIds,
    selectedDiscomSubstationIds,
  ]);

  // Discom Substation Load Data
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") {
      setDiscomSubLoadData([]);
      discomSubLoadFetchKeyRef.current = null;
      return;
    }
    if (!formattedRange) {
      setLoading(false);
      return;
    }
    if (selectedDiscomSubstationIds.length === 0) {
      setDiscomSubLoadData([]);
      setLoading(false);
      discomSubLoadFetchKeyRef.current = null;
      return;
    }

    const fetchKey = `${[...selectedDiscomSubstationIds].sort((a, b) => a - b).join(",")}|${formattedRange.startDate}|${formattedRange.endDate}`;
    if (discomSubLoadFetchKeyRef.current === fetchKey) {
      if (!discomSubLoadInFlightRef.current) setLoading(false);
      return;
    }
    discomSubLoadFetchKeyRef.current = fetchKey;

    let cancelled = false;
    setLoading(true);
    discomSubLoadInFlightRef.current = true;
    Promise.all(
      selectedDiscomSubstations.map((opt) =>
        getJvvnlLoadSummary({
          startDate: formattedRange.startDate,
          endDate: formattedRange.endDate,
          level: "discom_sub",
          ids: opt.value,
        }).then(({ rows }) =>
          rows.map((r) => ({ ...r, series_group: opt.label })),
        ),
      ),
    )
      .then((results) => {
        if (!cancelled || discomSubLoadFetchKeyRef.current === fetchKey)
          setDiscomSubLoadData(results.flat());
      })
      .catch((err) => {
        console.error("Error fetching JVVNL discom substation load:", err);
        if (!cancelled || discomSubLoadFetchKeyRef.current === fetchKey)
          setDiscomSubLoadData([]);
      })
      .finally(() => {
        discomSubLoadInFlightRef.current = false;
        if (!cancelled || discomSubLoadFetchKeyRef.current === fetchKey)
          setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedDiscomSubstationIds,
    selectedDiscomSubstations,
  ]);

  // Net Consumption: aggregated weather via /weather/aggregated/
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_net_consumption") {
      // weatherData is shared across modes; drop our key so a return to net
      // mode refetches rather than trusting another mode's overwrite.
      netWeatherFetchKeyRef.current = null;
      return;
    }
    if (!formattedRange) return;
    if (!selectedVariables.includes("Weather")) {
      setWeatherData([]);
      netWeatherFetchKeyRef.current = null;
      return;
    }
    if (!netCircle && !netTransco && !netDiscom) {
      setWeatherData([]);
      netWeatherFetchKeyRef.current = null;
      return;
    }
    const { level, ids } = resolveNetTarget(
      netDiscom,
      netTransco,
      netCircle,
      netCircleId,
    );
    const fetchKey = `${level}|${(Array.isArray(ids) ? ids : [ids]).join(",")}|${formattedRange.startDate}|${formattedRange.endDate}|${forecastDateOverride ?? ""}|${netSeriesLabel}`;
    if (netWeatherFetchKeyRef.current === fetchKey) return;
    netWeatherFetchKeyRef.current = fetchKey;

    let cancelled = false;
    getJvvnlAggregatedWeather({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      level,
      ids,
    })
      .then(({ rows }) => {
        if (cancelled && netWeatherFetchKeyRef.current !== fetchKey) return;
        // Weather is 15-min (1..96); demand/load-summary slot is 30-min (1..48).
        // Map 15-min time_block → 30-min slot: slot = ceil(time_block / 2)
        setWeatherData(
          rows.map((r) => ({
            ...r,
            seriesId: "net",
            seriesLabel: netSeriesLabel,
            // In forecast mode the demand rows are dated to the model's
            // forecast date, so re-stamp weather to the same day or the
            // date_block join key won't match and the weather line vanishes.
            date: forecastDateOverride ?? r.date,
            block: Math.ceil(((r.time_block as number) || 0) / 2),
            time_block: r.time_block,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setWeatherData([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    netCircle,
    netTransco,
    netDiscom,
    netCircleId,
    netSeriesLabel,
    forecastDateOverride,
  ]);

  // Net Consumption: use /load-summary/ endpoint (demand + generation + net)
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_net_consumption") {
      setNetLoadSummary([]);
      netLoadFetchKeyRef.current = null;
      return;
    }
    if (!formattedRange) {
      setLoading(false);
      return;
    }
    // Nothing selected (circle cleared and no transco/discom) → no data.
    if (!netCircle && !netTransco && !netDiscom) {
      setNetLoadSummary([]);
      setLoading(false);
      netLoadFetchKeyRef.current = null;
      return;
    }
    const { level, ids } = resolveNetTarget(
      netDiscom,
      netTransco,
      netCircle,
      netCircleId,
    );
    const fetchKey = `${level}|${(Array.isArray(ids) ? ids : [ids]).join(",")}|${formattedRange.startDate}|${formattedRange.endDate}`;
    if (netLoadFetchKeyRef.current === fetchKey) {
      // Same query as a prior render. If that prior fetch is still genuinely in
      // flight let its .finally clear loading; if it was cancelled mid-flight by
      // a re-render that dedups to the same key, nothing else will, so clear it.
      if (!netLoadInFlightRef.current) setLoading(false);
      return;
    }
    netLoadFetchKeyRef.current = fetchKey;

    let cancelled = false;
    setLoading(true);
    netLoadInFlightRef.current = true;
    getJvvnlLoadSummary({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      level,
      ids,
    })
      .then(({ rows }) => {
        // Commit as long as our key is still the active one — a same-key
        // re-render cancels this run but starts no new fetch, so `cancelled`
        // alone would wrongly drop the only response.
        if (!cancelled || netLoadFetchKeyRef.current === fetchKey)
          setNetLoadSummary(rows);
      })
      .catch(() => {
        if (!cancelled || netLoadFetchKeyRef.current === fetchKey)
          setNetLoadSummary([]);
      })
      .finally(() => {
        netLoadInFlightRef.current = false;
        if (!cancelled || netLoadFetchKeyRef.current === fetchKey)
          setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    netCircle,
    netTransco,
    netDiscom,
    netCircleId,
  ]);

  // Net Consumption: demand forecast
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_net_consumption") {
      // forecastRows is shared across modes; drop our key so returning to net
      // mode refetches instead of trusting another mode's overwrite.
      netForecastFetchKeyRef.current = null;
      return;
    }
    if (!selectedVariables.includes("Forecast") || !formattedRange) {
      setForecastRows([]);
      netForecastFetchKeyRef.current = null;
      return;
    }
    const { level, ids } = resolveNetTarget(
      netDiscom,
      netTransco,
      netCircle,
      netCircleId,
    );
    const forecastFetchKey = `${level}|${(Array.isArray(ids) ? ids : [ids]).join(",")}|${formattedRange.startDate}|${formattedRange.endDate}|${forecastDateOverride ?? ""}|${netVariable ?? ""}|${(forecastVariants ?? []).join(",")}|${selectedFunction}`;
    if (netForecastFetchKeyRef.current === forecastFetchKey) return;
    netForecastFetchKeyRef.current = forecastFetchKey;
    const baseLabel =
      selectedFunction === "Cumulative" ? "Total" : netSeriesLabel;
    const variants: {
      tag: "Demand" | "Net" | "Generation";
      pick: (p: any) => number;
      varName: string;
    }[] = [
      { tag: "Demand", pick: (p) => p.demand_mw, varName: "Demand" },
      { tag: "Generation", pick: (p) => p.generation_mw, varName: "Generation" },
      { tag: "Net", pick: (p) => p.net_mw, varName: "Net Load" },
    ];
    // forecastVariants (Forecast tab) or netVariable (single-select) narrows
    // the emitted variants; without either, emit all three.
    const wanted =
      forecastVariants && forecastVariants.length > 0
        ? forecastVariants
        : netVariable
          ? [netVariable]
          : null;
    const activeVariants = wanted
      ? variants.filter((v) => wanted.includes(v.varName))
      : variants;

    let cancelled = false;
    const forecastArgs = forecastDateOverride
      ? { date: forecastDateOverride }
      : {
          start_date: formattedRange.startDate,
          end_date: formattedRange.endDate,
        };
    // Wrapper already fetches generation and sums multi-plant rows into demand_mw/generation_mw/net_mw.
    getJvvnlDemandForecast({ level, ids, ...forecastArgs })
      .then((demandRes) => {
        if (cancelled && netForecastFetchKeyRef.current !== forecastFetchKey)
          return;
        const fallbackDate =
          demandRes.forecast_date || formattedRange.startDate;
        const out: any[] = [];
        for (const dRow of demandRes.rows) {
          const merged = {
            demand_mw: dRow.demand_mw,
            generation_mw: dRow.generation_mw,
            net_mw: dRow.net_mw,
          };
          // Pin to the override date when set so demand + weather rows share
          // the same date_block join key (weather is stamped to it too).
          const d = forecastDateOverride ?? dRow.date ?? fallbackDate;
          for (const v of activeVariants) {
            out.push({
              substationId: `${baseLabel} (${v.tag})`,
              substationName: `${baseLabel} ${v.tag}`,
              date: d,
              block: dRow.slot,
              month: getMonthStr(d),
              forecast_mw: v.pick(merged),
            });
          }
        }
        setForecastRows(out);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching JVVNL forecasts:", err);
        setForecastRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    netCircle,
    netTransco,
    netDiscom,
    netCircleId,
    selectedFunction,
    netSeriesLabel,
    netVariable,
    forecastVariants,
    forecastDateOverride,
  ]);

  // Transco Substation Data (via /load-summary/ level=transco)
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_substation") {
      setGssLoadData([]);
      gssLoadFetchKeyRef.current = null;
      return;
    }
    if (!formattedRange || selectedSubstationIds.length === 0) {
      setGssLoadData([]);
      gssLoadFetchKeyRef.current = null;
      return;
    }

    const fetchKey = `${[...selectedSubstationIds].sort((a, b) => a - b).join(",")}|${formattedRange.startDate}|${formattedRange.endDate}`;
    if (gssLoadFetchKeyRef.current === fetchKey) {
      if (!gssLoadInFlightRef.current) setLoading(false);
      return;
    }
    gssLoadFetchKeyRef.current = fetchKey;

    let cancelled = false;
    setLoading(true);
    gssLoadInFlightRef.current = true;

    Promise.all(
      selectedSubstationOptions.map((opt) =>
        getJvvnlLoadSummary({
          startDate: formattedRange.startDate,
          endDate: formattedRange.endDate,
          level: "transco",
          ids: opt.value,
        }).then(({ rows }) =>
          rows.map((r) => ({ ...r, series_group: opt.label })),
        ),
      ),
    )
      .then((results) => {
        if (!cancelled || gssLoadFetchKeyRef.current === fetchKey)
          setGssLoadData(results.flat());
      })
      .catch((err) => {
        console.error("Error fetching Transco load summary:", err);
        if (!cancelled || gssLoadFetchKeyRef.current === fetchKey)
          setGssLoadData([]);
      })
      .finally(() => {
        gssLoadInFlightRef.current = false;
        if (!cancelled || gssLoadFetchKeyRef.current === fetchKey)
          setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedSubstationIds,
    selectedSubstationOptions,
  ]);

  // Circle Data (via /load-summary/ level=circle)
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom") {
      setDiscomData([]);
      circleLoadFetchKeyRef.current = null;
      return;
    }
    if (!formattedRange || !selectedCircle) {
      setDiscomData([]);
      circleLoadFetchKeyRef.current = null;
      return;
    }

    const circleId = Number(selectedCircle?.value ?? NET_CONSUMPTION_CIRCLE_ID);
    const fetchKey = `${circleId}|${formattedRange.startDate}|${formattedRange.endDate}`;
    if (circleLoadFetchKeyRef.current === fetchKey) {
      if (!circleLoadInFlightRef.current) setLoading(false);
      return;
    }
    circleLoadFetchKeyRef.current = fetchKey;

    let cancelled = false;
    setLoading(true);
    circleLoadInFlightRef.current = true;
    getJvvnlLoadSummary({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      level: "circle",
      ids: circleId,
    })
      .then(({ rows }) => {
        if (!cancelled || circleLoadFetchKeyRef.current === fetchKey)
          setDiscomData(rows);
      })
      .catch((err) => {
        console.error("Error fetching JVVNL circle data:", err);
        if (!cancelled || circleLoadFetchKeyRef.current === fetchKey)
          setDiscomData([]);
      })
      .finally(() => {
        circleLoadInFlightRef.current = false;
        if (!cancelled || circleLoadFetchKeyRef.current === fetchKey)
          setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formattedRange, selectedGroupBy.rowKey, selectedCircle]);

  // ============================================================================
  // 4. WEATHER & FORECAST FETCHING
  // ============================================================================
  // Weather — discom substation: one aggregated weather call per selected discom sub
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_discom_substation") return;
    if (!formattedRange || !selectedVariables.includes("Weather")) {
      setWeatherData([]);
      return;
    }
    if (selectedDiscomSubstations.length === 0) {
      setWeatherData([]);
      return;
    }
    let cancelled = false;
    Promise.all(
      selectedDiscomSubstations.map((opt) =>
        getJvvnlAggregatedWeather({
          startDate: formattedRange.startDate,
          endDate: formattedRange.endDate,
          level: "discom_sub",
          ids: opt.value,
        }).then(({ rows }) =>
          rows.map((r) => ({
            ...r,
            seriesId: opt.value,
            seriesLabel: opt.label,
            block: Math.ceil(((r.time_block as number) || 0) / 2),
          })),
        ),
      ),
    )
      .then((results) => {
        if (!cancelled) setWeatherData(results.flat());
      })
      .catch(() => {
        if (!cancelled) setWeatherData([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    selectedDiscomSubstations,
  ]);

  // Weather — feeder: aggregated weather by feeder's discom_substation_id
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (!formattedRange || !selectedVariables.includes("Weather")) {
      setWeatherData([]);
      return;
    }
    if (selectedFeederOptions.length === 0 || feeders.length === 0) {
      setWeatherData([]);
      return;
    }

    // Group selected feeders by their discom_substation_id
    const feederById = new Map(feeders.map((f) => [f.id, f]));
    const subToFeeders = new Map<
      number,
      { subName: string; feeders: { id: number; name: string }[] }
    >();
    for (const opt of selectedFeederOptions) {
      const f = feederById.get(opt.value);
      if (!f || f.discom_substation_id == null) continue;
      const existing = subToFeeders.get(f.discom_substation_id) || {
        subName: f.discom_substation_name,
        feeders: [],
      };
      existing.feeders.push({ id: f.id, name: f.name });
      subToFeeders.set(f.discom_substation_id, existing);
    }
    if (subToFeeders.size === 0) {
      setWeatherData([]);
      return;
    }

    let cancelled = false;
    Promise.all(
      Array.from(subToFeeders.entries()).map(([subId, { feeders: fdrs }]) =>
        getJvvnlAggregatedWeather({
          startDate: formattedRange.startDate,
          endDate: formattedRange.endDate,
          level: "discom_sub",
          ids: subId,
        }).then(({ rows }) =>
          fdrs.flatMap((fdr) =>
            rows.map((r) => ({
              ...r,
              seriesId: fdr.id,
              seriesLabel: fdr.name,
              block: Math.ceil(((r.time_block as number) || 0) / 2),
            })),
          ),
        ),
      ),
    )
      .then((results) => {
        if (!cancelled) setWeatherData(results.flat());
      })
      .catch((err) => {
        console.error("Error fetching feeder aggregated weather:", err);
        if (!cancelled) setWeatherData([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    selectedFeederOptions,
    feeders,
  ]);

  // Weather — other modes (circle / transco / generation)
  useEffect(() => {
    if (selectedGroupBy.rowKey === "group_net_consumption") return;
    if (selectedGroupBy.rowKey === "group_discom_substation") return;
    if (selectedGroupBy.rowKey === "group_feeder") return;
    if (
      !["group_discom", "group_substation", "group_generation"].includes(
        selectedGroupBy.rowKey,
      ) ||
      !formattedRange ||
      !selectedVariables.includes("Weather")
    ) {
      setWeatherData([]);
      return;
    }

    let cancelled = false;

    // Circle view: weather is aggregated by circle NAME (no single location), so
    // use the aggregated endpoint like Net Consumption — correct for any circle.
    if (selectedGroupBy.rowKey === "group_discom") {
      if (!selectedCircle) {
        setWeatherData([]);
        return;
      }
      getJvvnlAggregatedWeather({
        startDate: formattedRange.startDate,
        endDate: formattedRange.endDate,
        level: "circle",
        ids: Number(selectedCircle.value),
      })
        .then(({ rows }) => {
          if (cancelled) return;
          setWeatherData(
            rows.map((r) => ({
              ...r,
              seriesId: selectedCircle.value,
              seriesLabel: selectedCircle.label,
              date: forecastDateOverride ?? r.date,
              block: Math.ceil(((r.time_block as number) || 0) / 2),
              time_block: r.time_block,
            })),
          );
        })
        .catch(() => {
          if (!cancelled) setWeatherData([]);
        });
      return () => {
        cancelled = true;
      };
    }

    const weatherTargets =
      selectedGroupBy.rowKey === "group_substation"
        ? selectedSubstationOptions.map((o) => ({
            seriesId: o.value,
            seriesLabel: o.label,
            locationId: substationById.get(o.value)?.location_id ?? o.value,
          }))
        : selectedGroupBy.rowKey === "group_generation" &&
            selectedPlantOptions.length > 0
          ? selectedPlantOptions
              .map((o) => {
                const loc = plantLocation.get(o.value);
                return loc == null
                  ? null
                  : {
                      seriesId: `plant_${o.value}` as any,
                      seriesLabel: o.label,
                      locationId: loc,
                    };
              })
              .filter(
                (
                  v,
                ): v is {
                  seriesId: any;
                  seriesLabel: string;
                  locationId: number;
                } => v !== null,
              )
          : [
              {
                seriesId: selectedCircle?.value ?? 1,
                seriesLabel: selectedCircle?.label ?? "KOTPUTLI",
                locationId: selectedCircle?.value ?? 1,
              },
            ];

    if (weatherTargets.length === 0) {
      setWeatherData([]);
      return;
    }

    (async () => {
      try {
        const uniqueLocations = new Map<
          number,
          { seriesId: number; seriesLabel: string }[]
        >();
        for (const target of weatherTargets) {
          const existing = uniqueLocations.get(target.locationId) || [];
          existing.push({
            seriesId: target.seriesId,
            seriesLabel: target.seriesLabel,
          });
          uniqueLocations.set(target.locationId, existing);
        }

        const allWeatherData = (
          await Promise.all(
            Array.from(uniqueLocations.entries()).map(
              async ([locationId, seriesTargets]) => {
                const cacheKey = `${locationId}|${formattedRange.startDate}|${formattedRange.endDate}`;
                let rows = weatherCacheRef.current.get(cacheKey);
                if (!rows) {
                  rows = await getJvvnlWeatherByLocation({
                    startDate: formattedRange.startDate,
                    endDate: formattedRange.endDate,
                    locationId,
                  });
                  weatherCacheRef.current.set(cacheKey, rows);
                }
                return seriesTargets.flatMap((target) =>
                  rows.map((row) => ({
                    ...row,
                    seriesId: target.seriesId,
                    seriesLabel: target.seriesLabel,
                    block: (row.time_block as number) || 0,
                  })),
                );
              },
            ),
          )
        ).flat();

        if (!cancelled) setWeatherData(allWeatherData);
      } catch (err) {
        console.error("Error fetching weather data:", err);
        if (!cancelled) setWeatherData([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedSubstationOptions,
    substationById,
    selectedVariables,
    selectedPlantOptions,
    plantLocation,
    selectedCircle,
    forecastDateOverride,
  ]);

  // Forecast (circle / transco / discom_sub via /demand-forecast/)
  useEffect(() => {
    const rowKey = selectedGroupBy.rowKey;
    const isNetMode = rowKey === "group_net_consumption";
    const isFeederMode = rowKey === "group_feeder";
    if (isNetMode || isFeederMode) return; // handled by separate effects

    if (!selectedVariables.includes("Forecast") || !formattedRange) {
      setForecastRows([]);
      return;
    }

    const { startDate, endDate } = formattedRange;

    if (rowKey === "group_discom") {
      if (!selectedCircle) {
        setForecastRows([]);
        return;
      }
      const circleName = selectedCircle.label;
      let cancelled = false;
      getJvvnlDemandForecast({
        level: "circle",
        ids: Number(selectedCircle.value),
        start_date: startDate,
        end_date: endDate,
      })
        .then(({ rows, forecast_date }) => {
          if (cancelled) return;
          const d = forecast_date || startDate;
          setForecastRows(
            rows.map((p) => ({
              substationId: circleName,
              substationName: circleName,
              date: p.date || d,
              block: p.block,
              month: getMonthStr(p.date || d),
              forecast_mw: p.demand_mw,
            })),
          );
        })
        .catch((err) => {
          if (!cancelled) {
            console.error("Error fetching circle forecast:", err);
            setForecastRows([]);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    if (rowKey === "group_substation") {
      if (selectedSubstationOptions.length === 0) {
        setForecastRows([]);
        return;
      }
      let cancelled = false;
      Promise.all(
        selectedSubstationOptions.map((opt) =>
          getJvvnlDemandForecast({
            level: "transco",
            ids: opt.value,
            start_date: startDate,
            end_date: endDate,
          }).then(({ rows, forecast_date }) => {
            const d = forecast_date || startDate;
            return rows.map((p) => ({
              substationId: opt.value,
              substationName: opt.label,
              date: p.date || d,
              block: p.block,
              month: getMonthStr(p.date || d),
              forecast_mw: p.demand_mw,
            }));
          }),
        ),
      )
        .then((results) => {
          if (!cancelled) setForecastRows(results.flat());
        })
        .catch((err) => {
          if (!cancelled) {
            console.error("Error fetching transco forecast:", err);
            setForecastRows([]);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    if (rowKey === "group_discom_substation") {
      if (selectedDiscomSubstations.length === 0) {
        setForecastRows([]);
        return;
      }
      let cancelled = false;
      Promise.all(
        selectedDiscomSubstations.map((opt) =>
          getJvvnlDemandForecast({
            level: "discom_sub",
            ids: opt.value,
            start_date: startDate,
            end_date: endDate,
          }).then(({ rows, forecast_date }) => {
            const d = forecast_date || startDate;
            return rows.map((p) => ({
              substationId: opt.value,
              substationName: opt.label,
              date: p.date || d,
              block: p.block,
              month: getMonthStr(p.date || d),
              forecast_mw: p.demand_mw,
            }));
          }),
        ),
      )
        .then((results) => {
          if (!cancelled) setForecastRows(results.flat());
        })
        .catch((err) => {
          if (!cancelled) {
            console.error("Error fetching discom sub forecast:", err);
            setForecastRows([]);
          }
        });
      return () => {
        cancelled = true;
      };
    }

    if (rowKey === "group_generation") {
      if (selectedPlantOptions.length === 0) {
        setForecastRows([]);
        return;
      }
      let cancelled = false;
      Promise.all(
        selectedPlantOptions.map((plant) =>
          getJvvnlGenerationForecast({
            level: "plant",
            ids: plant.value,
            start_date: startDate,
            end_date: endDate,
          })
            .then(({ rows, forecast_date }) => {
              const d = forecast_date || startDate;
              // level=plant returns one row per slot; if API still returns
              // multi-row, sum per slot to be safe.
              const sumBySlot = new Map<
                string,
                { date: string; slot: number; gen: number }
              >();
              for (const p of rows) {
                const date = p.date || d;
                const key = `${date}|${p.slot}`;
                const cur = sumBySlot.get(key);
                if (cur) cur.gen += p.generation_mw;
                else
                  sumBySlot.set(key, {
                    date,
                    slot: p.slot,
                    gen: p.generation_mw,
                  });
              }
              return Array.from(sumBySlot.values()).map((p) => ({
                substationId: plant.value,
                substationName: plant.label,
                date: p.date,
                block: p.slot,
                month: getMonthStr(p.date),
                forecast_mw: p.gen,
              }));
            })
            .catch((err) => {
              console.error("Error fetching generation forecast:", err);
              return [] as any[];
            }),
        ),
      )
        .then((results) => {
          if (!cancelled) setForecastRows(results.flat());
        })
        .catch(() => {
          if (!cancelled) setForecastRows([]);
        });
      return () => {
        cancelled = true;
      };
    }

    setForecastRows([]);
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedSubstationOptions,
    selectedDiscomSubstations,
    selectedPlantOptions,
    plantLocation,
    selectedVariables,
    selectedCircle,
  ]);

  // Feeder forecast (via /jvvnl/api/feeder-demand-forecast/)
  useEffect(() => {
    if (selectedGroupBy.rowKey !== "group_feeder") return;
    if (!selectedVariables.includes("Forecast") || !formattedRange) {
      setForecastRows([]);
      return;
    }
    const feederIdsForFetch =
      selectedFeederIds.length > 0
        ? selectedFeederIds
        : !embeddedSeededRef.current
          ? (() => {
              const eid = resolveEmbeddedFeederId(embedded);
              return eid != null ? [eid] : [];
            })()
          : [];
    if (feederIdsForFetch.length === 0) {
      setForecastRows([]);
      return;
    }
    let cancelled = false;
    getJvvnlFeederForecastByDate({
      startDate: formattedRange.startDate,
      endDate: formattedRange.endDate,
      feederIds: feederIdsForFetch,
    })
      .then(({ data }) => {
        if (cancelled) return;
        setForecastRows(
          data.map((r) => ({
            substationId: r.feeder_id,
            substationName: r.feeder_name,
            date: r.date,
            block: r.slot,
            month: getMonthStr(r.date),
            forecast_mw: r.predicted_kw / 1000,
          })),
        );
      })
      .catch((err) => {
        console.error("Error fetching JVVNL feeder forecast:", err);
        if (!cancelled) setForecastRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formattedRange,
    selectedGroupBy.rowKey,
    selectedVariables,
    selectedFeederIds,
    embedded?.id,
    embedded?.level,
    embedded,
  ]);

  // ============================================================================
  // 5. CHART DATA FORMATTING & LEGENDS
  // ============================================================================
  const activeDataKey = useMemo(() => {
    if (selectedGroupBy.rowKey === "group_net_consumption") return "net_mw";
    const unitToKey: Record<string, string> = {
      KW: "demand_kw",
      KWh: "demand_kwh",
      KVA: "demand_kva",
      KVAh: "demand_kvah",
      MW: "demand_mw",
      MWh: "demand_mwh",
    };
    const aggOnly = (u: string) => (u === "MWh" ? "demand_mwh" : "demand_mw");
    if (
      selectedGroupBy.rowKey === "group_discom" ||
      selectedGroupBy.rowKey === "group_substation"
    )
      return aggOnly(selectedUnit);
    if (selectedGroupBy.rowKey === "group_discom_substation") {
      if (selectedUnit === "MWh") return "demand_mwh";
      if (selectedUnit === "KWh") return "demand_kwh";
      if (selectedUnit === "KW") return "demand_kw";
      return "demand_mw";
    }
    return unitToKey[selectedUnit] || "demand_mwh";
  }, [selectedGroupBy.rowKey, selectedUnit]);

  const convertForecastMwToUnit = useCallback(
    (valueMw: number) => {
      // All series are 30-min blocks (48/day) → energy = power × 0.5h = power / 2,
      // mirroring the actual generation conversion in getJvvnlGeneration.
      // LU = MWh / 100 = MW / 200.
      if (selectedGroupBy.rowKey === "group_generation") {
        if (selectedUnit === "KW") return valueMw * 1000;
        if (selectedUnit === "KWh") return (valueMw * 1000) / 2;
        if (selectedUnit === "MWh") return valueMw / 2;
        if (selectedUnit === "LU") return valueMw / 200;
        return valueMw;
      }
      if (selectedUnit === "KW") return valueMw * 1000;
      if (selectedUnit === "KWh") return (valueMw * 1000) / 2;
      if (selectedUnit === "MWh") return valueMw / 2;
      return valueMw;
    },
    [selectedUnit, selectedGroupBy.rowKey],
  );

  const forecastBySeriesXKey = useMemo(() => {
    // Cumulative collapses all entity forecast lines into one summed "Total"
    // line per xKey (forecast is additive demand), mirroring the actual-series
    // "Total" rollup. Net mode already emits demand/gen/net "Total" variants,
    // so it keeps its per-substationId keys.
    const cumulate =
      selectedFunction === "Cumulative" &&
      selectedGroupBy.rowKey !== "group_net_consumption";
    const grouped = new Map<string, number[]>();
    for (const row of forecastRows) {
      const xKey = getXKeyForInterval(row);
      const key = cumulate ? `Total__${xKey}` : `${row.substationId}__${xKey}`;
      const values = grouped.get(key) || [];
      values.push(convertForecastMwToUnit(row.forecast_mw));
      grouped.set(key, values);
    }
    const reduced = new Map<string, number>();
    grouped.forEach((values, key) =>
      // Cumulative sums; comparative averages duplicate points.
      reduced.set(
        key,
        cumulate
          ? values.reduce((sum, v) => sum + v, 0)
          : values.reduce((sum, v) => sum + v, 0) / values.length,
      ),
    );
    return reduced;
  }, [
    forecastRows,
    getXKeyForInterval,
    convertForecastMwToUnit,
    selectedFunction,
    selectedGroupBy.rowKey,
  ]);

  const appendForecastToRows = useCallback(
    (rows: any[]) => {
      if (!selectedVariables.includes("Forecast")) return rows;
      const enriched = rows.map((row) => {
        const xKey = getXKeyForInterval(row);
        const forecastCols = forecastSeries.reduce<
          Record<string, number | null>
        >((acc, series) => {
          acc[`forecast_${series.value}`] =
            forecastBySeriesXKey.get(`${series.value}__${xKey}`) ?? null;
          return acc;
        }, {});
        return { ...row, ...forecastCols };
      });

      // Collect xKeys already covered by actual rows
      const coveredXKeys = new Set(
        enriched.map((r: any) => getXKeyForInterval(r)),
      );

      // Build stub rows for forecast points that fall outside the actual date range
      const extraByXKey = new Map<string, any>();
      for (const fr of forecastRows) {
        if (selectedBlockSet && !selectedBlockSet.has(fr.block)) continue;
        const xKey = getXKeyForInterval(fr);
        if (coveredXKeys.has(xKey)) continue;
        if (!extraByXKey.has(xKey)) {
          extraByXKey.set(xKey, {
            date: fr.date,
            block: fr.block,
            month: fr.month,
          });
        }
        const row = extraByXKey.get(xKey);
        const cumulate =
          selectedFunction === "Cumulative" &&
          selectedGroupBy.rowKey !== "group_net_consumption";
        const colKey = cumulate
          ? "forecast_Total"
          : `forecast_${fr.substationId}`;
        const val = convertForecastMwToUnit(fr.forecast_mw);
        // Cumulative sums entities; comparative averages duplicate points.
        row[colKey] =
          row[colKey] == null
            ? val
            : cumulate
              ? row[colKey] + val
              : (row[colKey] + val) / 2;
      }

      if (extraByXKey.size === 0) return enriched;

      const extras = Array.from(extraByXKey.values()).sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        return (a.block ?? 0) - (b.block ?? 0);
      });
      return [...enriched, ...extras];
    },
    [
      selectedVariables,
      getXKeyForInterval,
      forecastSeries,
      forecastBySeriesXKey,
      forecastRows,
      convertForecastMwToUnit,
      selectedFunction,
      selectedGroupBy.rowKey,
      selectedBlockSet,
    ],
  );

  const enrichRowsWithWeather = useCallback(
    (rows: any[], getXKey: (row: any) => string) => {
      if (
        !selectedVariables.includes("Weather") ||
        weatherData.length === 0 ||
        weatherSeries.length === 0
      )
        return rows;

      // Cumulative pools every entity's weather into one "total" series,
      // matching the single weatherSeries entry; values are averaged below.
      const cumulate = selectedFunction === "Cumulative";
      const weatherBySeriesXKey = new Map<string, number[]>();
      for (const row of weatherData) {
        const rawValue = (row as any)[selectedWeatherParam];
        if (rawValue == null || isNaN(parseFloat(rawValue))) continue;
        const xKey = getXKey({
          ...row,
          block: row.block ?? row.time_block,
          month: row.month ?? (row.date ? getMonthStr(row.date) : ""),
        });
        const key = `${cumulate ? "total" : row.seriesId}__${xKey}`;
        const values = weatherBySeriesXKey.get(key) || [];
        values.push(parseFloat(rawValue));
        weatherBySeriesXKey.set(key, values);
      }

      return rows.map((row) => {
        const xKey = getXKey(row);
        const weatherCols: Record<string, number | null> = {};
        for (const series of weatherSeries) {
          const values = weatherBySeriesXKey.get(`${series.value}__${xKey}`);
          weatherCols[`weather_${selectedWeatherParam}_${series.value}`] =
            values && values.length > 0
              ? values.reduce((a, b) => a + b, 0) / values.length
              : null;
        }
        return { ...row, ...weatherCols };
      });
    },
    [
      selectedVariables,
      weatherData,
      weatherSeries,
      selectedWeatherParam,
      selectedFunction,
    ],
  );

  const genDataKey = useMemo<keyof JvvnlGenerationRow>(() => {
    if ((GEN_UNITS as readonly string[]).includes(selectedUnit))
      return unitDataKey(selectedUnit);
    return "generation_mw";
  }, [selectedUnit]);

  const genByPlantXKey = useMemo(() => {
    const out = new Map<string, number[]>();
    if (selectedGroupBy.rowKey !== "group_generation") return out;
    const filtered = generationData.filter((item) => {
      if (selectedPlantIds.length > 0)
        return selectedPlantIds.includes(item.plant);
      if (selectedPlantTypes.length > 0)
        return selectedPlantTypes.includes(item.plant_type);
      return true;
    });
    for (const r of filtered) {
      const xKey = getXKeyForInterval({ ...r, month: getMonthStr(r.date) });
      const plantName = (r as any).plant_name || `Plant ${r.plant}`;
      const key = `${plantName}__${xKey}`;
      const arr = out.get(key) || [];
      const v = (r as any)[genDataKey];
      if (v !== null && v !== undefined) arr.push(v as number);
      out.set(key, arr);
    }
    return out;
  }, [
    generationData,
    selectedGroupBy.rowKey,
    selectedPlantIds,
    selectedPlantTypes,
    getXKeyForInterval,
    genDataKey,
  ]);

  const generationPlantNames = useMemo(() => {
    const names = new Set<string>();
    for (const key of genByPlantXKey.keys()) {
      const plantName = key.split("__")[0];
      names.add(plantName);
    }
    return Array.from(names);
  }, [genByPlantXKey]);

  const appendGenerationToRows = useCallback(
    (rows: any[]) => {
      if (selectedGroupBy.rowKey !== "group_generation") return rows;
      return rows.map((row) => {
        const xKey = getXKeyForInterval(row);
        const cols: Record<string, number | null> = {};
        for (const plantName of generationPlantNames) {
          const arr = genByPlantXKey.get(`${plantName}__${xKey}`);
          cols[`gen_${plantName}`] =
            arr && arr.length > 0
              ? arr.reduce((a, b) => a + b, 0) / arr.length
              : null;
        }
        return { ...row, ...cols };
      });
    },
    [
      selectedGroupBy.rowKey,
      getXKeyForInterval,
      generationPlantNames,
      genByPlantXKey,
    ],
  );

  const legends = useMemo(() => {
    const out: any[] = [];
    if (
      selectedVariables.includes("Actual") &&
      selectedGroupBy.rowKey !== "group_generation"
    ) {
      chartKeys.forEach((key, idx) => {
        // Net-consumption keys carry a "(Demand)"/"(Generation)"/"(Net)"
        // suffix. Color by variant so a series keeps the same color whether
        // shown alone or alongside the others (index-based color shifted when
        // the set changed). Non-net keys fall back to positional color.
        const variantColor = key.includes("(Demand)")
          ? ExchangeColors[0 % ExchangeColors.length]
          : key.includes("(Net)")
            ? ExchangeColors[1 % ExchangeColors.length]
            : key.includes("(Generation)")
              ? ExchangeColors[2 % ExchangeColors.length]
              : null;
        out.push({
          dataKey: `${key}_${activeDataKey}`,
          name: `${normalizeDisplayLabelWithoutUndefined(key)}`,
          stroke: variantColor ?? ExchangeColors[idx % ExchangeColors.length],
          type: selectedChartType.value as ChartType,
          stackId:
            selectedChartType.stackId !== undefined ? "Actual" : undefined,
          dashFlagKey:
            selectedGroupBy.rowKey === "group_feeder"
              ? `${key}_modelled`
              : undefined,
        });
      });
    }
    if (selectedVariables.includes("Weather")) {
      weatherSeries.forEach((series, index) => {
        out.push({
          dataKey: `weather_${selectedWeatherParam}_${series.value}`,
          name: `${selectedWeatherMetricLabel} - ${series.label}`,
          unit: selectedWeatherParamMeta.unit || "",
          stroke: lightenColor(
            ExchangeColors[(index + 4) % ExchangeColors.length],
            10,
          ),
          type: ChartType.Line as ChartType,
          yAxisId: "right" as const,
          strokeDasharray: "4 4",
        });
      });
    }
    if (selectedGroupBy.rowKey === "group_generation") {
      const genOnly = true;
      const genSeries = genOnly ? chartKeys : generationPlantNames;
      genSeries.forEach((plantName: string, index: number) => {
        out.push({
          dataKey: genOnly ? `${plantName}_${genDataKey}` : `gen_${plantName}`,
          name: `${normalizeDisplayLabelWithoutUndefined(plantName)} (Generation)`,
          stroke: lightenColor(
            ExchangeColors[(index + 2) % ExchangeColors.length],
            5,
          ),
          type: genOnly
            ? (selectedChartType.value as ChartType)
            : (ChartType.Line as ChartType),
          stackId:
            genOnly && selectedChartType.stackId !== undefined
              ? "generation"
              : undefined,
          strokeDasharray: genOnly ? undefined : "2 2",
          dashFlagKey: genOnly ? `${plantName}_modelled` : undefined,
        });
      });
    }
    if (selectedVariables.includes("Forecast")) {
      forecastSeries.forEach((substation, index) => {
        // Net forecast keys carry a "(Demand)"/"(Generation)"/"(Net)" suffix —
        // color by variant (matching the actual series palette) so a series
        // keeps its color whether shown alone or with the others. Non-net
        // forecast (per-entity) falls back to positional color.
        const v = String(substation.value);
        const variantIdx = v.includes("(Demand)")
          ? 0
          : v.includes("(Net)")
            ? 1
            : v.includes("(Generation)")
              ? 2
              : null;
        out.push({
          dataKey: `forecast_${substation.value}`,
          name: `${substation.label} (Forecast)`,
          stroke: lightenColor(
            ExchangeColors[(variantIdx ?? index + 7) % ExchangeColors.length],
            5,
          ),
          type: ChartType.Line as ChartType,
          strokeDasharray: "6 3",
        });
      });
    }
    return out;
  }, [
    selectedVariables,
    selectedGroupBy.rowKey,
    selectedWeatherParam,
    selectedWeatherParamMeta,
    chartKeys,
    selectedChartType,
    activeDataKey,
    forecastSeries,
    weatherSeries,
    selectedWeatherMetricLabel,
    generationPlantNames,
    genDataKey,
  ]);

  // Main Chart Data Generation
  useEffect(() => {
    let processedRows: any[] = [];
    let aggregationKeys: string[] = [];
    let averageKeys: string[] = [];

    // Forecast-only fallback: pivot forecastRows when actual data absent.
    const renderForecastOnlyOrEmpty = (): boolean => {
      const fRows = selectedBlockSet
        ? forecastRows.filter((r) => selectedBlockSet.has(r.block))
        : forecastRows;
      const wRows = selectedBlockSet
        ? weatherData.filter((r: any) =>
            selectedBlockSet.has((r.block ?? r.time_block) as number),
          )
        : weatherData;
      const hasForecast =
        selectedVariables.includes("Forecast") && fRows.length > 0;
      const hasWeatherOnly =
        selectedVariables.includes("Weather") && wRows.length > 0;
      // No actual rows AND no forecast/weather rows to build a skeleton from.
      if (!hasForecast && !hasWeatherOnly) {
        setChartData([]);
        setChartKeys([]);
        setTableData([]);
        return true;
      }
      const byXKey = new Map<string, any>();
      // Seed skeleton from weather rows too — enrichRowsWithWeather only adds
      // columns to existing rows, so a weather-only view (no actual/forecast)
      // needs its own x-key rows or the chart renders empty.
      if (hasWeatherOnly) {
        for (const wr of wRows) {
          const seedRow = {
            date: wr.date,
            block: (wr.block ?? wr.time_block) as number,
            month: wr.month ?? (wr.date ? getMonthStr(wr.date) : ""),
          };
          const xKey = getXKeyForInterval(seedRow);
          if (!byXKey.has(xKey)) byXKey.set(xKey, seedRow);
        }
      }
      for (const fr of fRows) {
        const xKey = getXKeyForInterval(fr);
        if (!byXKey.has(xKey)) {
          byXKey.set(xKey, {
            date: fr.date,
            block: fr.block,
            month: fr.month,
          });
        }
        const cumulate =
          selectedFunction === "Cumulative" &&
          selectedGroupBy.rowKey !== "group_net_consumption";
        const colKey = cumulate
          ? "forecast_Total"
          : `forecast_${fr.substationId}`;
        const row = byXKey.get(xKey);
        const existing = row[colKey];
        const val = convertForecastMwToUnit(fr.forecast_mw);
        // Cumulative sums entities; comparative averages duplicate points.
        row[colKey] =
          existing == null
            ? val
            : cumulate
              ? existing + val
              : (existing + val) / 2;
      }
      const builtRows = Array.from(byXKey.values()).sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        return (a.block ?? 0) - (b.block ?? 0);
      });
      // Forecast-only rows still need weather columns merged in, otherwise the
      // weather line never renders on future/forecast dates.
      const enriched = enrichRowsWithWeather(builtRows, (row: any) =>
        getXKeyForInterval(row),
      );
      setChartData(enriched);
      setChartKeys([]);
      setTableData([]);
      return true;
    };

    const generationOnly = selectedGroupBy.rowKey === "group_generation";

    if (generationOnly) {
      const showActual = selectedVariables.includes("Actual");
      const matchesGenFilters = (r: JvvnlGenerationRow) => {
        if (selectedBlockSet && !selectedBlockSet.has(r.block)) return false;
        if (selectedPlantIds.length > 0)
          return selectedPlantIds.includes(r.plant);
        if (selectedPlantTypes.length > 0)
          return selectedPlantTypes.includes(r.plant_type);
        return true;
      };
      const hasActual = generationData.some(matchesGenFilters);
      // Forecast-only path: actual not requested, OR actual requested but absent for date.
      if (!showActual || !hasActual) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const filteredGen = generationData.filter(matchesGenFilters);
      if (filteredGen.length === 0) {
        setChartData([]);
        setChartKeys([]);
        setTableData([]);
        return;
      }
      const genRows = filteredGen.map((r) => ({
        ...r,
        month: getMonthStr(r.date),
        series_group:
          selectedFunction === "Cumulative"
            ? "Total"
            : (r as any).plant_name || `Plant ${r.plant}`,
      }));
      const genAggKeys = [
        "generation_kw",
        "generation_kwh",
        "generation_mw",
        "generation_mwh",
        "generation_lu",
      ];
      const genFmt = FormatChartData({
        keys: chartGroupingKeys,
        tableKeys: ["date", "block", "month"],
        groupBy: ["series_group"],
        volumeKey: "",
        aggregationKeys: genAggKeys,
        averageKeys: genAggKeys,
        averageDuplicationKeys: ["date", "block"],
        priceKey: "",
        data: genRows,
      });
      const genBase = genFmt.data || [];

      // Modelled-flag aggregation: AND of is_generation_modified across all
      // raw rows in each (xKey, series_group) bucket. Used to dash chart line
      // segments at modelled points.
      const genModelledMap = new Map<string, boolean>();
      for (const r of genRows) {
        const xKey = getXKeyForInterval(r);
        const sg = (r as any).series_group;
        const key = `${xKey}__${sg}`;
        const pointModelled = !!(r as any).is_generation_modified;
        const prev = genModelledMap.get(key);
        genModelledMap.set(
          key,
          prev === undefined ? pointModelled : prev && pointModelled,
        );
      }
      if (genModelledMap.size > 0) {
        for (const row of genBase) {
          const xKey = getXKeyForInterval(row);
          for (const k of genFmt.keys) {
            const flag = genModelledMap.get(`${xKey}__${k}`);
            if (flag) (row as any)[`${k}_modelled`] = true;
          }
        }
      }

      // Enrich weather after appending forecast stubs so future-date rows get weather too.
      const genWithForecast = appendForecastToRows(genBase);
      const enrichedGen = enrichRowsWithWeather(genWithForecast, (row: any) =>
        getXKeyForInterval(row),
      );

      setChartData(enrichedGen);
      setChartKeys(genFmt.keys);
      setTableData(genFmt.tableData || []);
      return;
    }

    if (selectedGroupBy.rowKey === "group_net_consumption") {
      const showActual = selectedVariables.includes("Actual");
      if (!showActual) {
        renderForecastOnlyOrEmpty();
        return;
      }
      if (netLoadSummary.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? netLoadSummary.filter((r) => selectedBlockSet.has(r.block))
        : netLoadSummary;
      const baseLabel =
        selectedFunction === "Cumulative" ? "Total" : netSeriesLabel;
      processedRows = rows.flatMap((r) => {
        const common = {
          date: r.date,
          slot: r.slot,
          block: r.block,
          month: getMonthStr(r.date),
        };
        // discom_sub net values are raw kW; circle/transco are real MW. Plot the
        // matching magnitude so the chart axis agrees with the metric cards.
        // Energy units (KWh/MWh) plot per-slot energy; power units (KW/MW) plot
        // instantaneous load. Aggregation rule (sum vs avg) is set below to match.
        const isEnergy = selectedUnit === "MWh" || selectedUnit === "KWh";
        const netVal = netIsKw
          ? isEnergy
            ? r.net_load_kwh
            : r.net_load_kw
          : isEnergy
            ? r.net_load_mwh
            : r.net_load_mw;
        const demVal = netIsKw
          ? isEnergy
            ? r.demand_kwh
            : r.demand_kw
          : isEnergy
            ? r.demand_mwh
            : r.demand_mw;
        const genVal = netIsKw
          ? isEnergy
            ? r.generation_kwh
            : r.generation_kw
          : isEnergy
            ? r.generation_mwh
            : r.generation_mw;
        const all = [
          {
            ...common,
            net_mw: netVal,
            series_group: `${baseLabel} (Net)`,
            _var: "Net Load",
          },
          {
            ...common,
            net_mw: demVal,
            series_group: `${baseLabel} (Demand)`,
            _var: "Demand",
          },
          {
            ...common,
            net_mw: genVal,
            series_group: `${baseLabel} (Generation)`,
            _var: "Generation",
          },
        ];
        if (actualVariants && actualVariants.length > 0)
          return all.filter((x) => actualVariants.includes(x._var));
        return netVariable ? all.filter((x) => x._var === netVariable) : all;
      });
      // Energy (KWh/MWh) sums across slots; power (KW/MW) averages.
      const netIsEnergy = selectedUnit === "MWh" || selectedUnit === "KWh";
      aggregationKeys = ["net_mw"];
      averageKeys = netIsEnergy ? [] : ["net_mw"];
    } else if (selectedGroupBy.rowKey === "group_discom_substation") {
      if (discomSubLoadData.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? discomSubLoadData.filter((r) => selectedBlockSet.has(r.block))
        : discomSubLoadData;
      processedRows = rows.map((r) => ({
        ...r,
        month: getMonthStr(r.date),
        series_group:
          selectedFunction === "Cumulative" ? "Total" : r.series_group,
      }));
      aggregationKeys = ["demand_kw", "demand_kwh", "demand_mw", "demand_mwh"];
      averageKeys = ["demand_kw", "demand_mw"];
    } else if (selectedGroupBy.rowKey === "group_discom") {
      if (discomData.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? discomData.filter((r) => selectedBlockSet.has(r.block))
        : discomData;
      processedRows = rows.map((r) => ({
        ...r,
        month: getMonthStr(r.date),
        series_group:
          selectedFunction === "Cumulative"
            ? "Total"
            : (selectedCircle?.label ?? "KOTPUTLI"),
      }));
      aggregationKeys = ["demand_mw", "demand_mwh"];
      averageKeys = ["demand_mw"];
    } else if (selectedGroupBy.rowKey === "group_substation") {
      if (gssLoadData.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? gssLoadData.filter((r) => selectedBlockSet.has(r.block))
        : gssLoadData;
      processedRows = rows.map((r) => ({
        ...r,
        month: getMonthStr(r.date),
        series_group:
          selectedFunction === "Cumulative" ? "Total" : r.series_group,
      }));
      aggregationKeys = ["demand_mw", "demand_mwh"];
      averageKeys = ["demand_mw"];
    } else {
      if (rawData.length === 0) {
        renderForecastOnlyOrEmpty();
        return;
      }
      const rows = selectedBlockSet
        ? rawData.filter((r) => selectedBlockSet.has(r.block))
        : rawData;
      console.log(
        "[HSM] chart build effectiveFeederIds",
        effectiveFeederIds,
        "rawData",
        rawData.length,
      );
      const filtered = rows.filter(
        (item) =>
          effectiveFeederIds.length === 0 ||
          effectiveFeederIds.includes(item.feeder),
      );
      processedRows = filtered.map((item) => {
        const groupValue = item.feeder_name || `Feeder ${item.feeder}`;
        return {
          ...item,
          demand_mw: item.demand_kw / 1000,
          demand_mwh: item.demand_kwh / 1000,
          month: getMonthStr(item.date),
          series_group:
            selectedFunction === "Cumulative" ? "Total" : groupValue,
        };
      });
      aggregationKeys = [
        "demand_kw",
        "demand_kwh",
        "demand_kva",
        "demand_kvah",
        "demand_mw",
        "demand_mwh",
      ];
      averageKeys = ["demand_kw", "demand_kva", "demand_mw"];
    }

    const cData = FormatChartData({
      keys: chartGroupingKeys,
      tableKeys: ["date", "block", "month"],
      groupBy: ["series_group"],
      volumeKey: "",
      aggregationKeys,
      averageKeys,
      averageDuplicationKeys: ["date", "block"],
      priceKey: "",
      data: processedRows,
    });

    // Modelled-flag aggregation: OR of is_demand_kw_modelled /
    // is_demand_kva_modelled across all raw rows in each (xKey, series_group)
    // bucket. Used to dash chart line segments at modelled points.
    const modelledIsFeeder = selectedGroupBy.rowKey === "group_feeder";
    const modelledMap = new Map<string, boolean>();
    if (modelledIsFeeder) {
      for (const r of processedRows) {
        const xKey = getXKeyForInterval(r);
        const sg = (r as any).series_group;
        const key = `${xKey}__${sg}`;
        const pointModelled =
          !!(r as any).is_demand_kw_modelled ||
          !!(r as any).is_demand_kva_modelled;
        const prev = modelledMap.get(key);
        modelledMap.set(
          key,
          prev === undefined ? pointModelled : prev || pointModelled,
        );
      }
    }

    const withModelled = cData.data || [];

    if (modelledIsFeeder && modelledMap.size > 0) {
      for (const row of withModelled) {
        const xKey = getXKeyForInterval(row);
        for (const k of cData.keys) {
          const flag = modelledMap.get(`${xKey}__${k}`);
          if (flag) (row as any)[`${k}_modelled`] = true;
        }
      }
    }

    // Enrich weather AFTER appending forecast stubs so that forecast-only rows
    // (e.g. future dates with no actual data) also get weather data.
    const withForecast = appendForecastToRows(withModelled);
    const enriched = enrichRowsWithWeather(withForecast, (row: any) =>
      getXKeyForInterval(row),
    );

    setChartData(appendGenerationToRows(enriched));
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    rawData,
    gssLoadData,
    discomData,
    selectedGroupBy.rowKey,
    selectedBlock,
    selectedFunction,
    selectedInterval,
    chartGroupingKeys,
    effectiveFeederIds,
    enrichRowsWithWeather,
    getXKeyForInterval,
    appendForecastToRows,
    appendGenerationToRows,
    generationData,
    selectedPlantIds,
    selectedPlantTypes,
    selectedBlockSet,
    selectedVariables,
    feeders,
    discomSubLoadData,
    selectedDiscomSubstationIds,
    selectedDiscomSubstations,
    netLoadSummary,
    netSeriesLabel,
    netVariable,
    actualVariants,
    forecastRows,
    convertForecastMwToUnit,
    weatherData,
    selectedCircle,
    netIsKw,
  ]);

  // ============================================================================
  // 6. RENDER (derived values consumed by the render shell)
  // ============================================================================
  const xDataKey =
    selectedInterval.key === "time" ? "block" : selectedInterval.key;
  const secondDataKey = () => (selectedInterval.isTime ? "date" : undefined);

  const blockDurationMinutes = useMemo(() => {
    const sources: { block?: number }[] =
      selectedGroupBy.rowKey === "group_net_consumption"
        ? netLoadSummary
        : selectedGroupBy.rowKey === "group_discom_substation"
          ? discomSubLoadData
          : selectedGroupBy.rowKey === "group_discom"
            ? discomData
            : selectedGroupBy.rowKey === "group_substation"
              ? gssLoadData
              : selectedGroupBy.rowKey === "group_generation"
                ? generationData
                : rawData;
    let maxBlock = 0;
    for (const r of sources) {
      if (r.block != null && r.block > maxBlock) maxBlock = r.block;
    }
    return maxBlock > 48 ? 15 : 30;
  }, [
    selectedGroupBy.rowKey,
    netLoadSummary,
    discomSubLoadData,
    discomData,
    gssLoadData,
    generationData,
    rawData,
  ]);
  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  const showGenerationSearch = selectedGroupBy.rowKey === "group_generation";
  // Parent→child separator between hierarchy SearchBoxes (CIRCLE › TRANSCO › …).
  const hierSepClass =
    "text-gray-400 text-xl leading-none select-none self-center";
  const showNetConsumptionSearch =
    selectedGroupBy.rowKey === "group_net_consumption";
  const showFeederSearch = selectedGroupBy.rowKey === "group_feeder";
  const showCircleSearch = selectedGroupBy.rowKey === "group_discom";
  const showDiscomSubstationSearch =
    selectedGroupBy.rowKey === "group_discom_substation";
  const showSubstationSearch =
    selectedGroupBy.rowKey === "group_substation" ||
    (selectedVariables.includes("Forecast") &&
      selectedGroupBy.rowKey !== "group_discom" &&
      selectedGroupBy.rowKey !== "group_discom_substation" &&
      selectedGroupBy.rowKey !== "group_feeder" &&
      selectedGroupBy.rowKey !== "group_generation" &&
      selectedGroupBy.rowKey !== "group_net_consumption");

  // Circle-scoped views require a circle. When the user clears it, show the
  // empty/no-data screen instead of falling back to a default circle's data.
  const noCircleSelected =
    (showNetConsumptionSearch && !netCircle) ||
    ((showCircleSearch ||
      showFeederSearch ||
      showDiscomSubstationSearch ||
      showSubstationSearch) &&
      !selectedCircle);

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: selectedInterval.isTime
      ? ["block", selectedInterval.values[1]]
      : [selectedInterval.values[0]],
    extraLabels: selectedInterval.values.map((v) =>
      v === "date" ? "Date" : v,
    ),
    filename: AddDatesWithFileName({
      fileName: "JVVNL Overview",
      dates: currentRangeDate,
    }),
    showCriticalPeriods: selectedInterval.isTime,
    block_key: "block",
  };

  const feederFull = feederMetrics?.metrics?.full;
  const feederPeakLoadKw =
    feederFull?.peak_mw != null ? Number(feederFull.peak_mw) : null;
  const feederAvgLoadKw =
    feederFull?.avg_mw != null ? Number(feederFull.avg_mw) : null;
  const feederPeakTemp =
    feederFull?.peak_weather?.temp != null
      ? Number(feederFull.peak_weather.temp)
      : null;
  const feederAvgTemp =
    feederFull?.avg_weather?.temp != null
      ? Number(feederFull.avg_weather.temp)
      : null;

  const groupFull = groupMetrics?.metrics?.full;
  const groupPeakLoad =
    groupFull?.peak_mw != null ? Number(groupFull.peak_mw) : null;
  const groupAvgLoad =
    groupFull?.avg_mw != null ? Number(groupFull.avg_mw) : null;
  const groupPeakTemp =
    groupFull?.peak_weather?.temp != null
      ? Number(groupFull.peak_weather.temp)
      : null;
  const groupAvgTemp =
    groupFull?.avg_weather?.temp != null
      ? Number(groupFull.avg_weather.temp)
      : null;
  const groupLoadUnit =
    selectedGroupBy.rowKey === "group_discom_substation" ? "kW" : "MW";
  const groupLabelPrefix =
    selectedGroupBy.rowKey === "group_substation"
      ? "Transco"
      : selectedGroupBy.rowKey === "group_discom_substation"
        ? "Discom Substation"
        : "Circle";

  // Embedded mode: local view tab (Chart/Summary/Table)
  const [embeddedView, setEmbeddedView] = useState<
    "Chart" | "Summary" | "Table"
  >("Chart");

  // Embedded mode: seed plant selection from caller-provided entity
  const embeddedGenSeededRef = useRef(false);
  useEffect(() => {
    if (!embedded || embedded.level !== "plant") return;
    if (embeddedGenSeededRef.current) return;
    if (allPlants.length === 0) return;
    const plant = allPlants.find((p) => p.id === embedded.id);
    if (!plant) return;
    setSelectedPlantTypeOptions([
      { value: plant.plant_type, label: plant.plant_type },
    ]);
    setSelectedPlantOptions([{ value: plant.id, label: plant.name }]);
    didInitPlantDefaultsRef.current = true;
    embeddedGenSeededRef.current = true;
  }, [embedded, allPlants]);

  // Embedded mode: seed transco/discom from caller-provided entity
  const embeddedSeededRef = useRef(false);
  useEffect(() => {
    if (!embedded) return;
    if (embeddedSeededRef.current) return;
    if (embedded.level === "feeder") {
      const embeddedFeederId = Number(embedded.id);
      if (Number.isNaN(embeddedFeederId)) return;
      if (feeders.length === 0) {
        if (selectedFeederOptions.length === 0) {
          setSelectedFeederOptions([
            { value: embeddedFeederId, label: embedded.label },
          ]);
        }
        return;
      }
      const raw = feeders.find((f) => f.id === embeddedFeederId);
      if (raw) {
        setSelectedFeederOptions([{ value: raw.id, label: raw.name }]);
        if (raw.discom_substation_name) {
          setSelectedFeederSubstations([
            {
              value: raw.discom_substation_name,
              label: raw.discom_substation_name,
            },
          ]);
        }
      } else {
        setSelectedFeederOptions([
          { value: embeddedFeederId, label: embedded.label },
        ]);
      }
      embeddedSeededRef.current = true;
      return;
    }
    if (embedded.level !== "plant" && discomSubstations.length === 0) return;
    if (embedded.level === "circle") {
      // Net-mode fetches identify the circle by NAME (resolveNetTarget reads
      // netCircle.value as the name; the /load-summary/ + /demand-forecast/
      // endpoints match on name, not id). Seed value with the circle name
      // (embedded.label), NOT embedded.id.
      const value = embedded.label;
      // Avoid replacing netCircle with an identical-but-new object — that would
      // refire every net-mode fetch effect a second time on mount.
      setNetCircle((prev) =>
        prev && prev.value === value && prev.label === embedded.label
          ? prev
          : { value, label: embedded.label },
      );
      setNetTransco(null);
      setNetDiscom(null);
      embeddedSeededRef.current = true;
    } else if (embedded.level === "transco") {
      const value = Number(embedded.id);
      setNetTransco((prev) =>
        prev && prev.value === value && prev.label === embedded.label
          ? prev
          : { value, label: embedded.label },
      );
      setNetDiscom(null);
      embeddedSeededRef.current = true;
    } else if (embedded.level === "discom_sub") {
      const raw = discomSubstations.find(
        (d) => d.discom_substation_id === Number(embedded.id),
      );
      setNetDiscom({ value: Number(embedded.id), label: embedded.label });
      if (raw?.transco_substation_id) {
        setNetTransco({
          value: raw.transco_substation_id,
          label:
            raw.transco_substation_name ||
            `Transco ${raw.transco_substation_id}`,
        });
      }
      embeddedSeededRef.current = true;
    }
  }, [embedded, discomSubstations, feeders, selectedFeederOptions.length]);

  useEffect(() => {
    if (embedded?.level !== "feeder") return;
    if (selectedDiscomSubstations.length > 0) return;
    if (selectedTranscoForDiscom.length > 0) return;
    if (selectedFeederOptions.length === 0) return;
    if (feeders.length === 0 || discomSubstations.length === 0) return;
    const feederId = selectedFeederOptions[0]?.value;
    const feeder = feeders.find((f) => f.id === feederId);
    if (!feeder || feeder.discom_substation_id == null) return;
    const discom = discomSubstations.find(
      (d) => d.discom_substation_id === feeder.discom_substation_id,
    );
    if (!discom) return;
    setSelectedDiscomSubstations([
      {
        value: discom.discom_substation_id,
        label: discom.discom_substation_name,
      },
    ]);
    if (discom.transco_substation_id) {
      setSelectedTranscoForDiscom([
        {
          value: discom.transco_substation_id,
          label:
            discom.transco_substation_name ||
            `Transco ${discom.transco_substation_id}`,
        },
      ]);
    }
  }, [
    embedded,
    selectedDiscomSubstations.length,
    selectedTranscoForDiscom.length,
    selectedFeederOptions,
    feeders,
    discomSubstations,
  ]);

  // Notify the host popup when the embedded feeder selection settles on a
  // single feeder so its metric cards can refetch. Ref keeps the callback out
  // of the deps and dedupes repeat emits for the same feeder.
  const lastEmittedFeederRef = useRef<number | null>(null);
  const onEmbeddedFeederChangeRef = useRef(onEmbeddedFeederChange);
  onEmbeddedFeederChangeRef.current = onEmbeddedFeederChange;
  const onEmbeddedFeederListChangeRef = useRef(onEmbeddedFeederListChange);
  onEmbeddedFeederListChangeRef.current = onEmbeddedFeederListChange;
  useEffect(() => {
    if (embedded?.level !== "feeder") return;
    const cb = onEmbeddedFeederChangeRef.current;
    const single =
      selectedFeederOptions.length === 1 ? selectedFeederOptions[0] : null;
    const id = single ? single.value : null;
    // Always emit the full list for the multi-select heading.
    onEmbeddedFeederListChangeRef.current?.(
      selectedFeederOptions.map((o) => ({ id: o.value, name: o.label })),
    );
    if (!cb) return;
    if (id === lastEmittedFeederRef.current) return;
    lastEmittedFeederRef.current = id;
    cb(single ? { id: single.value, name: single.label } : null);
  }, [embedded?.level, selectedFeederOptions]);

  // Net mode (circle/transco/discom_sub): emit the deepest selected target so
  // the host can sync metric cards + heading with the in-chart selector.
  const lastEmittedNetRef = useRef<string | null>(null);
  const onEmbeddedNetChangeRef = useRef(onEmbeddedNetChange);
  onEmbeddedNetChangeRef.current = onEmbeddedNetChange;
  useEffect(() => {
    const lvl = embedded?.level;
    if (lvl !== "circle" && lvl !== "transco" && lvl !== "discom_sub") return;
    const cb = onEmbeddedNetChangeRef.current;
    if (!cb) return;
    const target = netDiscom
      ? {
          level: "discom_sub" as const,
          id: netDiscom.value,
          name: netDiscom.label,
        }
      : netTransco
        ? {
            level: "transco" as const,
            id: netTransco.value,
            name: netTransco.label,
          }
        : netCircle
          ? {
              level: "circle" as const,
              id: undefined,
              name: netCircle.label,
            }
          : null;
    const sig = target ? `${target.level}:${target.id}:${target.name}` : null;
    if (sig === lastEmittedNetRef.current) return;
    lastEmittedNetRef.current = sig;
    cb(target);
  }, [embedded?.level, netDiscom, netTransco, netCircle]);

  return {
    state: {
      selectedUnit,
      selectedGroupBy,
      selectedBlock,
      selectedChartType,
      selectedInterval,
      criticalPeriodKinds,
      selectedFunction,
      selectedVariables,
      selectedWeatherParams,
      feederOpenSignal,
      selectedFeederOptions,
      selectedSubstationOptions,
      selectedFeederSubstations,
      selectedCircle,
      selectedFeederTransco,
      netCircle,
      netTransco,
      netDiscom,
      discomSubstations,
      selectedTranscoForDiscom,
      selectedDiscomSubstations,
      netMetrics,
      genMetrics,
      allPlants,
      selectedPlantTypeOptions,
      selectedPlantOptions,
      selectedGenCircle,
      chartData,
      loading,
      circleOptions,
      embeddedView,
    },
    setters: {
      setSelectedUnit,
      setSelectedGroupBy,
      setSelectedBlock,
      setSelectedChartType,
      setSelectedInterval,
      setCriticalPeriodKinds,
      setSelectedFunction,
      setSelectedVariables,
      setSelectedWeatherParams,
      setSelectedFeederOptions,
      setSelectedSubstationOptions,
      setSelectedFeederSubstations,
      setSelectedCircle,
      setSelectedFeederTransco,
      setNetCircle,
      setNetTransco,
      setNetDiscom,
      setSelectedTranscoForDiscom,
      setSelectedDiscomSubstations,
      setSelectedPlantTypeOptions,
      setSelectedPlantOptions,
      setSelectedGenCircle,
      setEmbeddedView,
      setCpActive,
    },
    derived: {
      selectedWeatherParam,
      selectedWeatherMetricLabel,
      legends,
      visibleLegends,
      handleLegendSelect,
      selectedLegends,
      xDataKey,
      secondDataKey,
      blockDurationMinutes,
      hierSepClass,
      showGenerationSearch,
      showNetConsumptionSearch,
      showFeederSearch,
      showCircleSearch,
      showDiscomSubstationSearch,
      showSubstationSearch,
      noCircleSelected,
      tablePar,
      feederPeakLoadKw,
      feederAvgLoadKw,
      feederPeakTemp,
      feederAvgTemp,
      groupPeakLoad,
      groupAvgLoad,
      groupPeakTemp,
      groupAvgTemp,
      groupLoadUnit,
      groupLabelPrefix,
      generationOnlyMode,
      netIsKw,
      dataTab,
      plantOptions,
      plantTypeOptions,
      substationOptions,
      feederSubstationOptions,
      netCircleOptions,
      netTranscoOptions,
      netDiscomOptions,
      transcoOptionsForDiscom,
      filteredDiscomSubstationOptions,
      filteredFeederOptions,
      cpActive,
    },
    internal: {
      // Symbols the standalone-only deep-link effects + redux writes (which
      // stay in FeederDemand for Task 3b) still need. Not part of the render
      // contract — kept separate so state/setters/derived stay clean.
      effectiveTabKey,
      DefaultDates,
      feeders,
      feedersLoaded,
      substations,
      substationsLoaded,
      plantsLoaded,
      discomSubstationsLoaded,
      appliedDeepLinkRef,
      didInitPlantDefaultsRef,
      didInitDiscomSubDefaultsRef,
      didInitFeederDefaultsRef,
    },
  };
}
